import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DirectoryExtractor } from './components/DirectoryExtractor';
import { Backups } from './components/Backups';
import { SourceSheets } from './components/SourceSheets';
import EnrichmentProxySettings from './components/EnrichmentProxySettings';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  BellRing,
  CalendarClock,
  ChevronRight,
  CheckCircle2,
  Columns3,
  Database,
  Download,
  FileText,
  FileUp,
  FolderArchive,
  KeyRound,
  ListChecks,
  LogOut,
  MailCheck,
  RefreshCcw,
  RefreshCw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Upload,
  ServerCog,
  UserPlus,
  Users,
  X
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
const LeadDetailsModal = lazy(() => import('./components/LeadDetailsModal').then((module) => ({ default: module.LeadDetailsModal })));
import { LeadTable, type LeadTableColumn } from './components/LeadTable';
import { ImportFileReview } from './components/ImportFileReview';
import { EnrichmentQueue } from './components/EnrichmentQueue';
import type { WorkbookSheetPlan } from './lib/api';
import { request } from './lib/api';
import { PasteLeadPreview } from './components/PasteLeadPreview';
import {
  type BulkLeadFieldUpdate,
  bulkUpdateFields,
  bulkUpdateFilteredFields,
  changePassword,
  completeOutlookPstChunkUpload,
  createCampaign,
  createMailLogSource,
  createSingleLead,
  deleteMailLogSource,
  createUser,
  downloadFile,
  exportPath,
  enrichDataSource,
  enrichWorkspaceMissingDetails,
  verifyRetailIndustries,
  getEnrichmentWorkspaceSummary,
  getAnalytics,
  getDataSourceSummary,
  getJob,
  getJobsBatch,
  getOutlookPstParserStatus,
  getPerformanceSnapshot,
  getStatusUpdateBatch,
  getMe,
  getStats,
  listAuditLogs,
  listCampaigns,
  listDataSources,
  listFollowUps,
  listJobHistory,
  listLeads,
  countLeads,
  listRecoverableStatusChanges,
  listStatusUpdateBatches,
  listMailLogSources,
  listSuppression,
  listUsers,
  login,
  logout,
  pauseEnrichmentJob,
  importExtractedLeads,
  pasteLeads,
  pasteSuppressionLeads,
  previewMediaExtraction,
  queueLeadExport,
  retryFailedEnrichmentJob,
  rollbackBulkJob,
  restoreRecoverableStatusChanges,
  resumeEnrichmentJob,
  scheduleFollowUps,
  setAuthToken,
  syncMailLogSource,
  updateMailLogSource,
  updateLeadDetails,
  updateFollowUpStatus,
  updateLeadStatus,
  uploadDeduplication,
  uploadLeadImport,
  initOutlookPstChunkUpload,
  uploadOutlookPstChunk,
  uploadStatusCsv
} from './lib/api';
import {
  dataTypeLabels,
  leadDataTypes,
  leadStatuses,
  statusLabels,
  type AnalyticsPayload,
  type AppUser,
  type AuditLog,
  type Campaign,
  type CurrentUser,
  type DataSource,
  type DataSourceOutcomeSummary,
  type DashboardStats,
  type EnrichmentWorkspaceSummary,
  type ExtractedLeadDraft,
  type Job,
  type Lead,
  type LeadDataType,
  type LeadDetailsUpdate,
  type LeadFilters,
  type LeadStatus,
  type MailLogSource,
  type PerformanceSnapshot,
  type RecoverableStatusChange,
  type StatusUpdateBatch,
  type StatusUpdateBatchDetail,
  type FollowUpSummary,
  type FollowUpTask,
  type FollowUpTaskStatus,
  type SuppressedEmail,
  type UserRole
} from './lib/types';

type ActiveView =
  | 'directoryExtractor'
  | 'leads'
  | 'imports'
  | 'paste'
  | 'mediaExtractor'
  | 'dedupe'
  | 'sources'
  | 'followups'
  | 'enrichmentHub'
  | 'serverSync'
  | 'inboxSync'
  | 'campaigns'
  | 'analytics'
  | 'performance'
  | 'suppression'
  | 'audit'
  | 'backups'
  | 'settings';

type BulkUpdateTarget = 'status' | 'industry' | 'dataType' | 'campaignId' | 'tag';
type PstQueueStatus = 'waiting' | 'uploading' | 'paused' | 'processing' | 'completed' | 'failed';

interface PstQueueItem {
  id: string;
  file: File;
  name: string;
  size: number;
  uploadId?: string;
  uploadedChunks: number;
  chunkCount: number;
  progress: number;
  status: PstQueueStatus;
  jobId?: string;
  error?: string;
}

const viewPaths: Record<ActiveView, string> = {
  directoryExtractor: '/directory-extractor',
  leads: '/leads',
  imports: '/imports',
  paste: '/paste-leads',
  mediaExtractor: '/media-extractor',
  dedupe: '/dedupe',
  sources: '/sources',
  followups: '/follow-ups',
  enrichmentHub: '/enrichment-hub',
  serverSync: '/server-sync',
  inboxSync: '/inbox-sync',
  campaigns: '/campaigns',
  analytics: '/analytics',
  performance: '/performance',
  suppression: '/suppression',
  audit: '/audit',
  settings: '/settings',
  backups: '/backups'
};

function viewFromPath(pathname: string): ActiveView {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  const matchingView = (Object.entries(viewPaths) as Array<[ActiveView, string]>)
    .find(([, path]) => path === normalized)?.[0];
  return matchingView ?? 'leads';
}

const defaultFilters: LeadFilters = {
  limit: 100,
  preset: 'fresh'
};

function leadFiltersFromUrl(): LeadFilters {
  if (viewFromPath(window.location.pathname) !== 'leads') return defaultFilters;
  const params = new URLSearchParams(window.location.search);
  const filter: LeadFilters = { ...defaultFilters };
  const assignText = (key: keyof LeadFilters, param?: string) => {
    const value = params.get(param ?? String(key));
    if (value) {
      (filter as Record<string, unknown>)[key] = value;
    }
  };
  assignText('q');
  assignText('emailContains', 'contains');
  assignText('campaignId', 'campaign');
  assignText('country');
  assignText('industry');
  if (params.get('reviewOnly') === 'true') filter.reviewOnly = true;
  assignText('websiteUpdated');
  assignText('websiteUpdatedFrom');
  assignText('websiteUpdatedTo');
  assignText('timeZone');
  assignText('sourceContains', 'source');
  assignText('uploadDateFrom');
  assignText('uploadDateTo');
  assignText('sentDateFrom');
  assignText('sentDateTo');
  assignText('sourceJobId');
  assignText('sourceSheet');
  assignText('sourceFile');
  assignText('sourceUploadDate');
  assignText('sourceOwnerId');
  const sourceKind = params.get('sourceKind');
  if (sourceKind === 'file' || sourceKind === 'paste' || sourceKind === 'manual') filter.sourceKind = sourceKind;
  const status = params.get('status');
  if (status && leadStatuses.includes(status as LeadStatus)) filter.status = status as LeadStatus;
  const dataType = params.get('dataType');
  if (dataType && leadDataTypes.includes(dataType as LeadDataType)) filter.dataType = dataType as LeadDataType;
  const provider = params.get('provider');
  if (provider === 'google' || provider === 'microsoft' || provider === 'private') filter.provider = provider;
  const emailType = params.get('emailType');
  const enrichment = params.get('enrichment');
  if (enrichment && ['saved','failed','pending','full','partial','no_data','legacy'].includes(enrichment)) {
    filter.enrichment = enrichment as LeadFilters['enrichment'];
    filter.preset = '';
  }
  if (emailType === 'generic' || emailType === 'other') filter.emailType = emailType;
  if (params.get('industryMissing') === 'true') filter.industryMissing = true;
  const sortBy = params.get('sortBy');
  if (sortBy === 'lead' || sortBy === 'company' || sortBy === 'location' || sortBy === 'industry' || sortBy === 'dataType' || sortBy === 'source' || sortBy === 'status' || sortBy === 'sentDate' || sortBy === 'uploaded') filter.sortBy = sortBy;
  const sortDir = params.get('sortDir');
  if (sortDir === 'asc' || sortDir === 'desc') filter.sortDir = sortDir;
  const preset = params.get('preset');
  if (preset === 'fresh' || preset === 'unsent' || preset === '') filter.preset = preset as LeadFilters['preset'];
  const limit = Number(params.get('limit'));
  if ([100, 250, 500].includes(limit)) filter.limit = limit;
  return filter;
}

function leadFiltersToSearch(filters: LeadFilters) {
  const params = new URLSearchParams();
  const set = (key: string, value: unknown) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  };
  set('q', filters.q?.trim());
  set('contains', filters.emailContains?.trim());
  params.set('preset', filters.preset ?? '');
  set('status', filters.status);
  set('dataType', filters.dataType);
  set('country', filters.country?.trim());
  set('campaign', filters.campaignId);
  set('industry', filters.industry?.trim());
  if (filters.reviewOnly) set('reviewOnly', 'true');
  set('websiteUpdated', filters.websiteUpdated);
  set('websiteUpdatedFrom', filters.websiteUpdatedFrom);
  set('websiteUpdatedTo', filters.websiteUpdatedTo);
  if (filters.industryMissing) set('industryMissing', 'true');
  set('timeZone', filters.timeZone?.trim());
  set('provider', filters.provider);
  set('emailType', filters.emailType);
  set('enrichment', filters.enrichment);
  set('source', filters.sourceContains?.trim());
  set('uploadDateFrom', filters.uploadDateFrom);
  set('uploadDateTo', filters.uploadDateTo);
  set('sentDateFrom', filters.sentDateFrom);
  set('sentDateTo', filters.sentDateTo);
  set('sourceJobId', filters.sourceJobId);
  set('sourceSheet', filters.sourceSheet);
  set('sourceFile', filters.sourceFile);
  set('sourceUploadDate', filters.sourceUploadDate);
  set('sourceOwnerId', filters.sourceOwnerId);
  set('sourceKind', filters.sourceKind);
  set('sortBy', filters.sortBy);
  set('sortDir', filters.sortDir);
  if (filters.limit && filters.limit !== defaultFilters.limit) set('limit', filters.limit);
  const query = params.toString();
  return query ? `?${query}` : '';
}

const userRoles: UserRole[] = ['admin', 'manager', 'uploader', 'viewer'];
const countryOptions = ['USA', 'Canada', 'United Kingdom', 'Australia', 'India'];
const importFileTypes = '.csv,.txt,.xlsx,.xlsm';

const navItems: Array<{ view: ActiveView; icon: LucideIcon; label: string; group: string }> = [
  { view: 'directoryExtractor', icon: Search, label: 'Directory Extractor', group: 'Data' },
  { view: 'leads', icon: Database, label: 'Leads', group: 'Data' },
  { view: 'imports', icon: Upload, label: 'Imports', group: 'Data' },
  { view: 'paste', icon: FileText, label: 'Paste Leads', group: 'Data' },
  { view: 'mediaExtractor', icon: FileUp, label: 'Media Extractor', group: 'Data' },
  { view: 'dedupe', icon: ShieldCheck, label: 'Dedupe', group: 'Data' },
  { view: 'sources', icon: FolderArchive, label: 'Data Sources', group: 'Data' },
  { view: 'followups', icon: CalendarClock, label: 'Follow-ups', group: 'Outreach' },
  { view: 'serverSync', icon: ServerCog, label: 'Server Sync', group: 'Outreach' },
  { view: 'inboxSync', icon: MailCheck, label: 'Inbox Sync', group: 'Outreach' },
  { view: 'enrichmentHub', icon: Sparkles, label: 'Enrichment Hub', group: 'Outreach' },
  { view: 'campaigns', icon: ListChecks, label: 'Campaigns', group: 'Outreach' },
  { view: 'analytics', icon: BarChart3, label: 'Analytics', group: 'Workspace' },
  { view: 'performance', icon: Activity, label: 'Performance', group: 'Workspace' },
  { view: 'suppression', icon: ShieldCheck, label: 'Suppression', group: 'Workspace' },
  { view: 'audit', icon: Activity, label: 'Audit', group: 'Workspace' },
  { view: 'settings', icon: Users, label: 'Settings', group: 'Workspace' },
  { view: 'backups', icon: FolderArchive, label: 'Backups', group: 'Workspace' }
];

const navGroups = ['Data', 'Outreach', 'Workspace'];
const leadColumnLabels: Record<LeadTableColumn, string> = {
  company: 'Company', location: 'Location', industry: 'Industry', mailBackend: 'Mail backend', dataType: 'Data type', source: 'Source',
  uploaded: 'Uploaded date', campaign: 'Campaign', sentDate: 'Sent date', suppression: 'Suppression'
};

const viewMeta: Record<ActiveView, { eyebrow: string; title: string; summary: string }> = {
  directoryExtractor: { eyebrow: 'Business research', title: 'Directory extractor', summary: 'Public business records and extraction jobs.' },
  leads: { eyebrow: 'Lead workspace', title: 'Lead management console', summary: 'Find, qualify and maintain your outreach-ready lead inventory.' },
  imports: { eyebrow: 'Data intake', title: 'Import and enrich data', summary: 'Bring in lead files or delivery outcomes with reliable field matching.' },
  paste: { eyebrow: 'Fast intake', title: 'Paste structured leads', summary: 'Capture contacts from formatted research without losing their details.' },
  mediaExtractor: { eyebrow: 'AI intake', title: 'Media extractor', summary: 'Extract lead details from readable PDFs, documents, screenshots and business-card style files.' },
  dedupe: { eyebrow: 'Data quality', title: 'Create a clean outreach list', summary: 'Screen incoming contacts against your workspace and protected addresses.' },
  sources: { eyebrow: 'Source history', title: 'Files and pasted batches', summary: 'Reopen any saved data source with its full sheet and live status analysis.' },
  followups: { eyebrow: 'Outreach rhythm', title: 'Follow-up schedule', summary: 'Stay on top of source-level outreach actions without changing lead outcomes.' },
  serverSync: { eyebrow: 'Delivery automation', title: 'Mail server sync', summary: 'Connect PowerMTA accounting and logging files, then apply delivery outcomes automatically.' },
  inboxSync: { eyebrow: 'Inbox automation', title: 'Outlook PST sync', summary: 'Upload Outlook archives with resumable syncing and apply only negative, junk, bounce and failed outcomes.' },
  enrichmentHub: { eyebrow: 'Data intelligence', title: 'Enrichment hub', summary: 'Track website/domain enrichment jobs and open enriched source files from one place.' },
  campaigns: { eyebrow: 'Outreach organization', title: 'Campaign workspace', summary: 'Keep lead activity grouped around the outreach that matters.' },
  analytics: { eyebrow: 'Workspace intelligence', title: 'Performance overview', summary: 'See lead flow, delivery quality and reply outcomes at a glance.' },
  performance: { eyebrow: 'System health', title: 'Performance monitor', summary: 'Track slow API calls, route timing and admin-level system responsiveness.' },
  suppression: { eyebrow: 'Organization protection', title: 'Suppression controls', summary: 'Review permanently protected email addresses and their reasons.' },
  audit: { eyebrow: 'Administrator controls', title: 'Activity audit', summary: 'Review changes made across the organization workspace.' },
  settings: { eyebrow: 'Workspace settings', title: 'People and access', summary: 'Manage your profile, users and private workspaces.' },
  backups: { eyebrow: 'Platform recovery', title: 'Backups', summary: 'Snapshots and verified off-server copies.' }
};

const workflowSteps: Array<{ view: ActiveView; label: string; detail: string }> = [
  { view: 'imports', label: 'Import', detail: 'Bring in lists and delivery files' },
  { view: 'mediaExtractor', label: 'Extract', detail: 'Review leads from media files' },
  { view: 'dedupe', label: 'Clean', detail: 'Remove outreach conflicts' },
  { view: 'leads', label: 'Operate', detail: 'Search, update and export' }
];

function formatNumber(value: number | undefined): string {
  return new Intl.NumberFormat().format(value ?? 0);
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let size = value / 1024;
  let unit = units[0];
  for (let index = 1; index < units.length && size >= 1024; index += 1) {
    size /= 1024;
    unit = units[index];
  }
  return `${size >= 10 ? size.toFixed(0) : size.toFixed(1)} ${unit}`;
}

const pstChunkSize = 20 * 1024 * 1024;

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return '-';
  }
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: '2-digit',
    year: 'numeric'
  }).format(new Date(value));
}

function todayForInput(): string {
  const date = new Date();
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return offsetDate.toISOString().slice(0, 10);
}

function jobTitle(job: Job): string {
  if (job.jobType === 'source_enrichment') {
    const sourceFile = typeof job.options.sourceFile === 'string' ? job.options.sourceFile : job.fileName;
    return sourceFile ? `Enrich ${sourceFile}` : 'Website enrichment';
  }
  const labels: Record<Exclude<Job['jobType'], 'source_enrichment'>, string> = {
    lead_import: 'Lead import',
    deduplication: 'Deduplication',
    status_update: 'Status update',
    lead_export: 'Lead export',
    bulk_field_update: 'Bulk field update',
    mail_log_sync: 'Server sync'
  };
  return labels[job.jobType as Exclude<Job['jobType'], 'source_enrichment'>];
}

function jobProcessedRows(job: Job): number {
  return Math.min(job.totalRows, job.insertedRows + job.updatedRows + job.duplicateRows + job.invalidRows + job.suppressedRows + job.cleanRows);
}

function optionNumber(options: Record<string, unknown>, key: string): number | null {
  const value = options[key];
  const numeric = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(numeric) ? numeric : null;
}

function enrichmentJobMetrics(job: Job) {
  return {
    direct: optionNumber(job.options, 'fetchDirect') ?? 0,
    proxy: optionNumber(job.options, 'fetchProxy') ?? 0,
    failed: optionNumber(job.options, 'fetchFailed') ?? 0,
    cached: optionNumber(job.options, 'fetchCached') ?? 0,
    rpm: optionNumber(job.options, 'recordsPerMinute'),
    eta: optionNumber(job.options, 'etaMinutes')
  };
}

function nextSyncLabel(source: MailLogSource): string {
  if (!source.lastSyncAt) return 'Due now';
  const next = new Date(source.lastSyncAt).getTime() + source.intervalMinutes * 60_000;
  if (!Number.isFinite(next)) return 'Schedule ready';
  if (next <= Date.now()) return 'Due now';
  return `Next ${formatDateTime(new Date(next).toISOString())}`;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(value));
}

const pastedEmailPattern = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

function pastedTextToFile(text: string): { file: File; emailCount: number } {
  const emails = text.match(pastedEmailPattern) ?? [];
  const body = ['email', ...emails.map((email) => csvEscapeClient(email.toLowerCase()))].join('\n');
  return { file: new File([body], 'pasted-leads.csv', { type: 'text/csv' }), emailCount: emails.length };
}

function csvEscapeClient(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

const tableHeaderHints = new Set([
  'email', 'e-mail', 'email address', 'mail', 'name', 'full name', 'first name', 'last name',
  'phone', 'number', 'mobile', 'website', 'link', 'linkedin', 'company', 'designation',
  'title', 'country', 'city', 'state', 'street', 'zip', 'postal code', 'time zone',
  'employee size', 'industry', 'data type', 'status', 'sent date', 'upload date', 'campaign'
]);

function looksLikeTableHeader(cells: string[]): boolean {
  return cells.some((cell) => tableHeaderHints.has(cell.trim().toLowerCase()));
}

function fallbackHeadersForRows(rows: string[][]): string[] {
  const width = Math.max(1, ...rows.map((row) => row.length));
  return Array.from({ length: width }, (_, index) => (index === 0 ? 'email' : `column_${index + 1}`));
}

function pastedTableToFile(text: string): File {
  const lines = text
    .trim()
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0);
  const hasTabs = lines.some((line) => line.includes('\t'));

  if (hasTabs) {
    const rows = lines.map((line) => line.split('\t').map((cell) => cell.trim()));
    const normalizedRows = looksLikeTableHeader(rows[0] ?? []) ? rows : [fallbackHeadersForRows(rows), ...rows];
    const csv = normalizedRows.map((row) => row.map(csvEscapeClient).join(',')).join('\n');
    return new File([csv], 'pasted-import.csv', { type: 'text/csv' });
  }

  const firstLine = lines[0]?.split(',').map((cell) => cell.trim()) ?? [];
  const body = looksLikeTableHeader(firstLine) ? lines.join('\n') : ['email', ...lines].join('\n');
  return new File([body], 'pasted-import.csv', { type: 'text/csv' });
}

const pastedLeadExample = `Name: Melissa Willems
Email: mwvirtualsolutions@gmail.com
Number: (615) 267-8104
Website: https://mwbookkeepingandtax.com/
Company: MW Bookkeeping & Tax
LinkedIn: https://www.linkedin.com/in/melissa-willems
Designation: Owner
Country: USA
City: Albuquerque
State: New Mexico
Street: 123 Main Street
Zip: 87101
Time Zone: MST
Employee Size: 1-10
Industry: CPA
Status: Sent
Sent Date: 2026-09-13`;

function hasFilteredBulkScope(filters: LeadFilters): boolean {
  return Boolean(
    filters.q?.trim()
      || filters.emailContains?.trim()
      || filters.campaignId
      || filters.sentDateFrom
      || filters.sentDateTo
      || filters.uploadDateFrom
      || filters.uploadDateTo
      || filters.country?.trim()
      || filters.industry?.trim()
      || filters.websiteUpdated || filters.websiteUpdatedFrom || filters.websiteUpdatedTo
      || filters.timeZone?.trim()
      || filters.provider
      || filters.emailType
      || filters.enrichment
      || filters.dataType
      || filters.status
      || filters.preset
      || filters.sourceJobId
      || filters.sourceFile
      || filters.sourceContains?.trim()
  );
}

function LoginScreen({ onLogin }: { onLogin: (user: CurrentUser) => void }) {
  const [email, setEmail] = useState('admin@leadsgrowthpoint.com');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const session = await login(email, password);
      onLogin(session.user);
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : 'Login failed.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login-shell">
      <form className="login-card" onSubmit={handleSubmit}>
        <div className="brand-lockup login-brand">
          <div className="brand-mark">D</div>
          <div>
            <strong>Data Ops</strong>
            <span>leadsgrowthpoint.com</span>
          </div>
        </div>
        <label>
          Email
          <input value={email} onChange={(event) => setEmail(event.target.value)} />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
          />
        </label>
        {error && <div className="notice compact-notice">{error}</div>}
        <button className="primary-button" type="submit" disabled={loading}>
          <KeyRound size={17} />
          Sign In
        </button>
      </form>
    </main>
  );
}

function ProgressBar({ value }: { value: number }) {
  return (
    <div className="progress-bar" aria-label={`${value}% complete`}>
      <span style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }} />
    </div>
  );
}

function MailLogSyncProgress({ source }: { source: MailLogSource }) {
  const total = Math.max(0, source.syncTotalFiles ?? 0);
  const checked = Math.min(Math.max(0, source.syncProcessedFiles ?? 0), total);
  const percent = total > 0 ? Math.round((checked / total) * 100) : 0;

  if (source.lastSyncStatus !== 'running') {
    return <span className="muted-value">{total > 0 ? `${formatNumber(checked)} / ${formatNumber(total)} checked` : 'Waiting'}</span>;
  }

  return (
    <div className="mail-sync-progress" aria-label={`${source.name} sync ${percent}% complete`}>
      <div className="mail-sync-progress-header"><strong>{percent}%</strong><span>{formatNumber(checked)} / {formatNumber(total)} files</span></div>
      <ProgressBar value={percent} />
      <small>{source.syncCurrentFile ? `Checking ${source.syncCurrentFile}` : `${formatNumber(source.syncUpdatedRows)} lead updates applied`}</small>
    </div>
  );
}

function WorkflowGuide({ activeView, onNavigate }: { activeView: ActiveView; onNavigate: (view: ActiveView) => void }) {
  return (
    <section className="workflow-guide" aria-label="Workspace workflow">
      <div className="workflow-title">
        <span className="section-kicker"><Sparkles size={14} /> Workspace flow</span>
        <strong>Move from source data to outreach-ready records.</strong>
      </div>
      <ol>
        {workflowSteps.map((step, index) => {
          const current = activeView === step.view || (step.view === 'imports' && activeView === 'paste');
          return (
            <li className={current ? 'current' : ''} key={step.view}>
              <button type="button" onClick={() => onNavigate(step.view)}>
                <span className="workflow-number">0{index + 1}</span>
                <span><strong>{step.label}</strong><small>{step.detail}</small></span>
                <ChevronRight size={16} aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function SourceStatusChart({ source }: { source: DataSource }) {
  const segments = [
    { label: 'Unsent', value: source.unsentCount, className: 'source-unsent' },
    { label: 'Sent', value: source.sentCount, className: 'source-sent' },
    { label: 'Positive', value: source.positiveCount, className: 'source-positive' },
    { label: 'Negative', value: source.negativeCount, className: 'source-negative' },
    { label: 'Failed', value: source.failedCount, className: 'source-failed' },
    { label: 'Junk / bounce', value: source.junkCount + source.hardBounceCount, className: 'source-suppressed' }
  ].filter((segment) => segment.value > 0);

  return (
    <div className="source-chart" aria-label="Lead status distribution">
      <div className="source-chart-track">
        {segments.map((segment) => (
          <span
            className={segment.className}
            key={segment.label}
            style={{ width: `${Math.max((segment.value / Math.max(source.leadCount, 1)) * 100, 1)}%` }}
            title={`${segment.label}: ${formatNumber(segment.value)}`}
          />
        ))}
      </div>
      <div className="source-chart-legend">
        {segments.map((segment) => <span key={segment.label}>{segment.label} <strong>{formatNumber(segment.value)}</strong></span>)}
      </div>
    </div>
  );
}

function addDays(dateValue: string, days: number): string {
  const date = new Date(`${dateValue}T12:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function FollowUpScheduler({
  source,
  saving,
  onClose,
  onSchedule
}: {
  source: DataSource;
  saving: boolean;
  onClose: () => void;
  onSchedule: (sentDate: string, gapDays: number) => Promise<void>;
}) {
  const [sentDate, setSentDate] = useState(source.uploadDate || new Date().toISOString().slice(0, 10));
  const [gapDays, setGapDays] = useState(1);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    await onSchedule(sentDate, gapDays);
  }

  return (
    <div className="drawer-backdrop" onMouseDown={(event) => event.target === event.currentTarget && !saving && onClose()}>
      <form className="follow-up-dialog" onSubmit={submit}>
        <header>
          <div>
            <span className="section-kicker"><CalendarClock size={14} /> Follow-up plan</span>
            <h2>Schedule two follow-ups</h2>
            <p>{source.sourceFile}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} disabled={saving} aria-label="Close scheduler"><X size={19} /></button>
        </header>
        <div className="follow-up-form-grid">
          <label>
            Source send date
            <input type="date" required value={sentDate} onChange={(event) => setSentDate(event.target.value)} />
          </label>
          <label>
            Gap between follow-ups
            <input type="number" min="1" max="30" value={gapDays} onChange={(event) => setGapDays(Math.min(Math.max(Number(event.target.value) || 1, 1), 30))} />
          </label>
        </div>
        <div className="follow-up-preview">
          <span><strong>Follow-up 1</strong>{formatDate(addDays(sentDate, gapDays))}</span>
          <span><strong>Follow-up 2</strong>{formatDate(addDays(sentDate, gapDays * 2))}</span>
        </div>
        <footer>
          <button className="ghost-button" type="button" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="primary-button" type="submit" disabled={saving}>
            <CalendarClock size={17} />
            {saving ? 'Saving...' : 'Save Follow-up Plan'}
          </button>
        </footer>
      </form>
    </div>
  );
}

function JobsPanel({
  jobs,
  onDownload,
  onOpen,
  title = 'Recent jobs',
  subtitle = 'Imports, exports, bulk updates, clean files, and rejection reports'
}: {
  jobs: Job[];
  onDownload: (path: string, name: string) => void;
  onOpen: (job: Job) => void;
  title?: string;
  subtitle?: string;
}) {
  return (
    <section className="jobs-panel" aria-label={title}>
      <div className="jobs-heading">
        <h2>{title}</h2>
        <span>{subtitle}</span>
      </div>
      <div className="job-list">
        {jobs.length === 0 ? (
          <p className="empty-jobs">No jobs have run yet.</p>
        ) : (
          jobs.slice(0, 10).map((job) => (
            <article
              className="job-card"
              key={job.id}
              role="button"
              tabIndex={0}
              onClick={() => onOpen(job)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onOpen(job);
                }
              }}
            >
              <div>
                <strong>{jobTitle(job)}</strong>
                <span>{job.fileName}</span>
              </div>
              <div className={`job-state ${job.status} ${job.jobType === 'source_enrichment' && job.status === 'completed' && job.progressPercent < 100 ? 'partial' : ''}`}>
                {job.jobType === 'source_enrichment' && job.status === 'completed' && job.progressPercent < 100 ? 'partial' : job.status}
              </div>
              <div className="job-metrics">
                <span>{formatNumber(job.totalRows)} rows</span>
                {job.jobType === 'source_enrichment' ? (
                  <>
                    {(() => {
                      const metrics = enrichmentJobMetrics(job);
                      return (
                        <>
                          {metrics.rpm !== null && <span>{formatNumber(Math.round(metrics.rpm))}/min</span>}
                          {metrics.eta !== null && metrics.eta > 0 && <span>ETA {formatNumber(metrics.eta)} min</span>}
                          {metrics.proxy > 0 && <span>{formatNumber(metrics.proxy)} proxy</span>}
                          {metrics.cached > 0 && <span>{formatNumber(metrics.cached)} cached</span>}
                          {metrics.failed > 0 && <span>{formatNumber(metrics.failed)} fetch failed</span>}
                        </>
                      );
                    })()}
                    <span>{formatNumber(job.updatedRows)} enriched</span>
                    <span>{formatNumber(jobProcessedRows(job))} processed</span>
                    <span>{formatNumber(Math.max(job.totalRows - jobProcessedRows(job), 0))} remaining</span>
                  </>
                ) : job.jobType === 'deduplication' ? (
                  <span>{formatNumber(job.cleanRows)} clean</span>
                ) : job.jobType === 'lead_export' ? (
                  <span>{formatNumber(job.cleanRows)} exported</span>
                ) : job.jobType === 'bulk_field_update' ? (
                  <span>{formatNumber(job.updatedRows)} updated</span>
                ) : (
                  <span>{formatNumber(job.insertedRows + job.updatedRows)} affected</span>
                )}
                <span>{formatNumber(job.duplicateRows)} duplicates</span>
                <span>{formatNumber(job.suppressedRows)} suppressed</span>
                {job.jobType === 'deduplication' && (
                  optionNumber(job.options, 'dedupeCountsVersion') === 1 ? (
                    <span title="Unique matched emails with visible statuses at the time of this dedupe run. These counts are included in duplicates/suppressed, not additional rows.">
                      {formatNumber(optionNumber(job.options, 'dedupePositive') ?? 0)} positive ·{' '}
                      {formatNumber(optionNumber(job.options, 'dedupeNegative') ?? 0)} negative ·{' '}
                      {formatNumber(optionNumber(job.options, 'dedupeFailed') ?? 0)} failed
                    </span>
                  ) : <span title="This older job did not save status totals. Download its full report to inspect matched statuses.">Status breakdown not recorded</span>
                )}
              </div>
              <ProgressBar value={job.progressPercent ?? 0} />
              <div className="download-group" onClick={(event) => event.stopPropagation()}>
                {job.exportUrl && (
                  <>
                    <button type="button" onClick={() => onDownload(job.exportUrl!, job.jobType === 'source_enrichment' ? 'enriched-leads.csv' : 'clean-leads.csv')}>
                      <Download size={15} />
                      {job.jobType === 'source_enrichment' ? 'Enriched CSV' : 'Clean CSV'}
                    </button>
                    <button
                      type="button"
                      onClick={() => onDownload(`${job.exportUrl}?format=xlsx`, job.jobType === 'source_enrichment' ? 'enriched-leads.xlsx' : 'clean-leads.xlsx')}
                    >
                      <Download size={15} />
                      XLSX
                    </button>
                  </>
                )}
                {job.rejectedReportUrl && (
                  <button
                    type="button"
                    onClick={() => onDownload(job.rejectedReportUrl!, job.jobType === 'deduplication' ? 'full-dedupe-report.csv' : 'dedupe-report.csv')}
                  >
                    <FileText size={15} />
                    {job.jobType === 'deduplication' ? 'Full report' : 'Report'}
                  </button>
                )}
              </div>
            </article>
          ))
        )}
      </div>
    </section>
  );
}

function StatusUpdateBatchesPanel({
  batches,
  onOpen
}: {
  batches: StatusUpdateBatch[];
  onOpen: (batch: StatusUpdateBatch) => void;
}) {
  return (
    <section className="operation-panel" aria-label="Status update batches">
      <div className="panel-heading">
        <div>
          <h2>Status update batches</h2>
          <p>Outcome files stay separate from the original lead source.</p>
        </div>
        <span>{formatNumber(batches.length)} recent</span>
      </div>
      {batches.length === 0 ? (
        <div className="source-empty"><ListChecks size={22} /><h2>No status batches yet</h2></div>
      ) : (
        <div className="compact-table-shell">
          <table>
            <thead>
              <tr><th>Outcome file</th><th>Rows</th><th>Changed</th><th>Protected</th><th>New</th><th>Unmatched</th><th /></tr>
            </thead>
            <tbody>
              {batches.slice(0, 15).map((batch) => (
                <tr key={batch.id}>
                  <td><strong>{batch.fileName}</strong><small className="table-subtle">{formatDateTime(batch.createdAt)} · {batch.status}</small></td>
                  <td>{formatNumber(batch.totalRows)}<small className="table-subtle">{formatNumber(batch.validRows)} valid</small></td>
                  <td>{formatNumber(batch.updatedRows)}</td>
                  <td>{formatNumber(batch.protectedRows)}</td>
                  <td>{formatNumber(batch.insertedRows)}</td>
                  <td>{formatNumber(batch.unmatchedRows)}</td>
                  <td><button className="ghost-button" type="button" onClick={() => onOpen(batch)}>Report</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function StatusUpdateBatchDialog({
  batch,
  onClose
}: {
  batch: StatusUpdateBatchDetail;
  onClose: () => void;
}) {
  return (
    <div className="drawer-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="job-details-dialog" role="dialog" aria-modal="true" aria-label="Status update batch report">
        <header>
          <div>
            <span className="section-kicker"><ListChecks size={14} /> Status update report</span>
            <h2>{batch.fileName}</h2>
            <p>{formatDateTime(batch.createdAt)} · {batch.status}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close status update report"><X size={19} /></button>
        </header>
        <div className="job-detail-grid">
          <div><span>Sheet rows</span><strong>{formatNumber(batch.totalRows)}</strong></div>
          <div><span>Matched & changed</span><strong>{formatNumber(batch.updatedRows)}</strong></div>
          <div><span>Protected</span><strong>{formatNumber(batch.protectedRows)}</strong></div>
          <div><span>Created</span><strong>{formatNumber(batch.insertedRows)}</strong></div>
          <div><span>Unmatched</span><strong>{formatNumber(batch.unmatchedRows)}</strong></div>
          <div><span>Invalid / duplicate</span><strong>{formatNumber(batch.invalidRows + batch.duplicateRows)}</strong></div>
        </div>
        <div className="panel-heading"><div><h3>Original source breakdown</h3></div><span>{formatNumber(batch.sources.length)} sources</span></div>
        {batch.sources.length === 0 ? (
          <div className="source-empty"><FileText size={22} /><h2>Detailed tracking is not available for this older batch</h2></div>
        ) : (
          <div className="compact-table-shell">
            <table>
              <thead><tr><th>Original lead source</th><th>Rows</th><th>Changed</th><th>Protected</th><th>New</th><th>Unmatched</th></tr></thead>
              <tbody>{batch.sources.map((source) => (
                <tr key={`${source.sourceJobId ?? 'none'}-${source.sourceFile}-${source.sourceSheet ?? ''}`}>
                  <td><strong>{source.sourceFile}</strong>{source.sourceSheet && <small className="table-subtle">{source.sourceSheet}</small>}</td>
                  <td>{formatNumber(source.totalRows)}</td>
                  <td>{formatNumber(source.updatedRows)}</td>
                  <td>{formatNumber(source.protectedRows)}</td>
                  <td>{formatNumber(source.insertedRows)}</td>
                  <td>{formatNumber(source.unmatchedRows)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function JobDetailsDialog({
  job,
  onClose,
  onDownload,
  onRollback,
  onPause,
  onResume,
  onRetryFailed
}: {
  job: Job;
  onClose: () => void;
  onDownload: (path: string, name: string) => void;
  onRollback: (job: Job) => void;
  onPause: (job: Job) => void;
  onResume: (job: Job) => void;
  onRetryFailed: (job: Job) => void;
}) {
  const processed = jobProcessedRows(job);
  const remaining = Math.max(job.totalRows - processed, 0);
  const sourceFile = typeof job.options.sourceFile === 'string' ? job.options.sourceFile : null;
  const sourceJobId = typeof job.options.sourceJobId === 'string' ? job.options.sourceJobId : null;
  const isEnrichment = job.jobType === 'source_enrichment';
  const isExport = job.jobType === 'lead_export';
  const isBulkUpdate = job.jobType === 'bulk_field_update';
  const enrichmentMetrics = isEnrichment ? enrichmentJobMetrics(job) : null;
  const rollbackApplied = Boolean(job.options.rollbackApplied);
  const rollbackAvailable = isBulkUpdate && job.status === 'completed' && !rollbackApplied;

  return (
    <div className="drawer-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="job-details-dialog" role="dialog" aria-modal="true" aria-label={`${jobTitle(job)} details`}>
        <header>
          <div>
            <span className="section-kicker"><Sparkles size={14} /> {isEnrichment ? 'Enrichment activity' : 'Job activity'}</span>
            <h2>{jobTitle(job)}</h2>
            <p>{sourceFile || job.fileName}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close job details"><X size={19} /></button>
        </header>

        <div className="job-detail-status">
          <div>
            <span className={`job-state ${job.status}`}>{job.status}</span>
            <strong>{job.progressPercent}% complete</strong>
          </div>
          <ProgressBar value={job.progressPercent} />
        </div>

        <div className="job-detail-grid">
          <div><span>Total leads</span><strong>{formatNumber(job.totalRows)}</strong></div>
          <div><span>Processed</span><strong>{formatNumber(processed)}</strong></div>
          <div><span>Remaining</span><strong>{formatNumber(remaining)}</strong></div>
          {isEnrichment ? (
            <>
              <div><span>Enriched</span><strong>{formatNumber(job.updatedRows)}</strong></div>
              <div><span>No data found</span><strong>{formatNumber(job.duplicateRows)}</strong></div>
              <div><span>Could not process</span><strong>{formatNumber(job.invalidRows)}</strong></div>
              <div><span>Speed</span><strong>{enrichmentMetrics?.rpm ? `${formatNumber(Math.round(enrichmentMetrics.rpm))}/min` : '-'}</strong></div>
              <div><span>ETA</span><strong>{enrichmentMetrics?.eta ? `${formatNumber(enrichmentMetrics.eta)} min` : '-'}</strong></div>
              <div><span>Direct fetch</span><strong>{formatNumber(enrichmentMetrics?.direct ?? 0)}</strong></div>
              <div><span>Proxy fetch</span><strong>{formatNumber(enrichmentMetrics?.proxy ?? 0)}</strong></div>
              <div><span>Cached domains</span><strong>{formatNumber(enrichmentMetrics?.cached ?? 0)}</strong></div>
              <div><span>Fetch failed</span><strong>{formatNumber(enrichmentMetrics?.failed ?? 0)}</strong></div>
            </>
          ) : isExport ? (
            <>
              <div><span>Exported</span><strong>{formatNumber(job.cleanRows)}</strong></div>
              <div><span>Ready file</span><strong>{job.exportUrl ? 'Yes' : 'No'}</strong></div>
              <div><span>Format</span><strong>CSV / XLSX</strong></div>
            </>
          ) : isBulkUpdate ? (
            <>
              <div><span>Updated</span><strong>{formatNumber(job.updatedRows)}</strong></div>
              <div><span>Snapshot</span><strong>{rollbackApplied ? 'Rolled back' : 'Saved'}</strong></div>
              <div><span>Rollback</span><strong>{rollbackAvailable ? 'Available' : 'Closed'}</strong></div>
            </>
          ) : (
            <>
              <div><span>Created / updated</span><strong>{formatNumber(job.insertedRows + job.updatedRows)}</strong></div>
              <div><span>Duplicates</span><strong>{formatNumber(job.duplicateRows)}</strong></div>
              <div><span>Invalid / skipped</span><strong>{formatNumber(job.invalidRows + job.suppressedRows)}</strong></div>
            </>
          )}
        </div>

        {job.options.sheetResults && typeof job.options.sheetResults === 'object' ? <div style={{ overflowX: 'auto' }}><h3>Worksheet results</h3><table><thead><tr><th>Sheet</th><th>Rows</th><th>Inserted</th><th>Updated</th><th>Duplicates</th><th>Invalid</th><th>Suppressed</th></tr></thead><tbody>{Object.entries(job.options.sheetResults as Record<string, Record<string, number>>).map(([name, stats]) => <tr key={name}><td>{name}</td>{['rows','inserted','updated','duplicates','invalid','suppressed'].map(key => <td key={key}>{formatNumber(stats[key] ?? 0)}</td>)}</tr>)}</tbody></table></div> : null}
        <dl className="job-detail-timeline">
          <div><dt>Created</dt><dd>{formatDateTime(job.createdAt)}</dd></div>
          <div><dt>Started</dt><dd>{formatDateTime(job.startedAt)}</dd></div>
          <div><dt>{job.status === 'completed' ? 'Completed' : 'Last update'}</dt><dd>{formatDateTime(job.status === 'completed' ? job.completedAt : job.updatedAt)}</dd></div>
          {sourceJobId && <div><dt>Source ID</dt><dd>{sourceJobId}</dd></div>}
        </dl>

        {isEnrichment && (
          <p className="job-detail-note">Only blank profile fields are enriched. Existing status, upload date, and manually saved lead details stay unchanged.</p>
        )}
        {isEnrichment && (
          <div className="job-detail-actions">
            {(job.status === 'running' || job.status === 'queued') && <button className="ghost-button" type="button" onClick={() => onPause(job)}><CalendarClock size={15} />Pause after batch</button>}
            {(job.status === 'paused' || job.status === 'stuck' || job.status === 'failed') && <button className="ghost-button" type="button" onClick={() => onResume(job)}><RefreshCcw size={15} />Resume remaining</button>}
            {job.invalidRows > 0 && <button className="ghost-button" type="button" onClick={() => onRetryFailed(job)}><RefreshCw size={15} />Retry failed only</button>}
          </div>
        )}
        {isEnrichment && job.exportUrl && (
          <div className="job-detail-actions">
            <button type="button" onClick={() => onDownload(job.exportUrl!, 'enriched-leads.csv')}><Download size={15} />Download CSV</button>
            <button type="button" onClick={() => onDownload(`${job.exportUrl}?format=xlsx`, 'enriched-leads.xlsx')}><Download size={15} />Download XLSX</button>
          </div>
        )}
        {isExport && job.exportUrl && (
          <div className="job-detail-actions">
            <button type="button" onClick={() => onDownload(job.exportUrl!, 'leads-export.csv')}><Download size={15} />Download CSV</button>
            <button type="button" onClick={() => onDownload(`${job.exportUrl}?format=xlsx`, 'leads-export.xlsx')}><Download size={15} />Download XLSX</button>
          </div>
        )}
        {isBulkUpdate && (
          <div className="job-detail-actions">
            <button className="ghost-button" type="button" disabled={!rollbackAvailable} onClick={() => onRollback(job)}>
              <RefreshCcw size={15} />
              {rollbackApplied ? 'Rollback applied' : 'Rollback bulk update'}
            </button>
            <span>Rollback is available for one hour after completion.</span>
          </div>
        )}
        {job.errorMessage && <p className="job-detail-error">{job.errorMessage}</p>}
      </section>
    </div>
  );
}

export default function App() {
  const [authChecking, setAuthChecking] = useState(true);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [activeView, setCurrentActiveView] = useState<ActiveView>(() => viewFromPath(window.location.pathname));
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [statsRefreshing, setStatsRefreshing] = useState(false);
  const [statsUpdatedAt, setStatsUpdatedAt] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsPayload | null>(null);
  const [performance, setPerformance] = useState<PerformanceSnapshot | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filters, setFilters] = useState<LeadFilters>(() => leadFiltersFromUrl());
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const [tableDensity, setTableDensity] = useState<'comfortable' | 'compact'>('compact');
  const [visibleColumns, setVisibleColumns] = useState<Set<LeadTableColumn>>(
    () => new Set(['company', 'location', 'industry', 'mailBackend', 'dataType', 'source', 'sentDate'])
  );
  const [currentCursor, setCurrentCursor] = useState<string | undefined>();
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [leadTotalCount, setLeadTotalCount] = useState<number | null>(null);
  const [leadCountError, setLeadCountError] = useState<string | null>(null);
  const [leadCountRetry, setLeadCountRetry] = useState(0);
  const [totalsOpen, setTotalsOpen] = useState(() => localStorage.getItem('lead-totals-open') === 'true');
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [allMatchedSelected, setAllMatchedSelected] = useState(false);
  const [bulkUpdateTarget, setBulkUpdateTarget] = useState<BulkUpdateTarget>('status');
  const [bulkStatusValue, setBulkStatusValue] = useState<LeadStatus>('sent');
  const [bulkIndustryValue, setBulkIndustryValue] = useState('');
  const [bulkDataTypeValue, setBulkDataTypeValue] = useState<LeadDataType>('cpa');
  const [bulkCampaignValue, setBulkCampaignValue] = useState('');
  const [bulkTagValue, setBulkTagValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<() => void>(() => undefined);
  const leadRequestSeq = useRef(0);
  const [notice, setNotice] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [enrichmentSummary, setEnrichmentSummary] = useState<EnrichmentWorkspaceSummary | null>(null);
  const [enrichmentSummaryLoading, setEnrichmentSummaryLoading] = useState(false);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [suppression, setSuppression] = useState<SuppressedEmail[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [sourceNextCursor, setSourceNextCursor] = useState<string | null>(null);
  const [sourceLoadingMore, setSourceLoadingMore] = useState(false);
  const [sourceCatalogLoading, setSourceCatalogLoading] = useState(false);
  const [sourceCatalogError, setSourceCatalogError] = useState('');
  const [sourceQuery, setSourceQuery] = useState('');
  const [sourceKindFilter, setSourceKindFilter] = useState<'all' | DataSource['kind']>('all');
  const [sourceReplyFilter, setSourceReplyFilter] = useState<'all' | 'positive' | 'negative' | 'both' | 'no_positive' | 'no_negative'>('all');
  const [activeCatalogSourceKey, setActiveCatalogSourceKey] = useState<string | null>(null);
  const [mobileSourceDetail, setMobileSourceDetail] = useState(false);
  const [activeSourceSummary, setActiveSourceSummary] = useState<{ key: string; summary: DataSourceOutcomeSummary } | null>(null);
  const [activeSourceSummaryLoading, setActiveSourceSummaryLoading] = useState(false);
  const [activeSourceSummaryError, setActiveSourceSummaryError] = useState<string | null>(null);
  const [sourceSummaryRevision, setSourceSummaryRevision] = useState(0);
  const sourceDirectoryActiveKeyRef = useRef<string | null>(null);
  const sourceDirectoryListRef = useRef<HTMLDivElement | null>(null);
  const sourceMoreRef = useRef<HTMLDivElement | null>(null);
  const sourceRequestScopeRef = useRef('');
  const sourceLoadedScopeRef = useRef('');
  const sourceCountsQueueRef = useRef<Promise<void>>(Promise.resolve());
  const sourceLoadingMoreRef = useRef(false);
  const [followUps, setFollowUps] = useState<FollowUpTask[]>([]);
  const [followUpSummary, setFollowUpSummary] = useState<FollowUpSummary>({ dueToday: 0, overdue: 0, upcoming: 0 });
  const [followUpSource, setFollowUpSource] = useState<DataSource | null>(null);
  const [mailLogSources, setMailLogSources] = useState<MailLogSource[]>([]);
  const [selectedMailLogSourceIds, setSelectedMailLogSourceIds] = useState<Set<string>>(new Set());
  const [mailLogBatchProgress, setMailLogBatchProgress] = useState<{ total: number; completed: number; processedFiles: number; updatedRows: number; failures: string[] } | null>(null);
  const [activeLead, setActiveLead] = useState<Lead | null>(null);
  const [activeJob, setActiveJob] = useState<Job | null>(null);
  const [statusBatches, setStatusBatches] = useState<StatusUpdateBatch[]>([]);
  const [activeStatusBatch, setActiveStatusBatch] = useState<StatusUpdateBatchDetail | null>(null);

  const [leadFiles, setLeadFiles] = useState<Array<{ file: File; plans: WorkbookSheetPlan[] | null; status: 'ready' | 'uploading' | 'submitted' | 'failed'; error?: string }>>([]);
  const updateFilePlans = useCallback((file: File, plans: WorkbookSheetPlan[] | null) => {
    setLeadFiles(current => current.map(item => item.file === file ? { ...item, plans } : item));
  }, []);
  const [leadCampaign, setLeadCampaign] = useState('');
  const [leadDefaultStatus, setLeadDefaultStatus] = useState<LeadStatus>('unsent');
  const [leadDataType, setLeadDataType] = useState<LeadDataType | ''>('');
  const [leadDefaultCountry, setLeadDefaultCountry] = useState('');
  const [leadDefaultSentDate, setLeadDefaultSentDate] = useState('');
  const [leadUploadDate, setLeadUploadDate] = useState('');
  const [leadPasteText, setLeadPasteText] = useState('');
  const [leadImportError, setLeadImportError] = useState('');
  const leadImportPanelRef = useRef<HTMLElement | null>(null);
  const leadFileSectionsRef = useRef(new Map<File, HTMLElement>());
  const leadImportRunningRef = useRef(false);
  const [leadDragActive, setLeadDragActive] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteCampaign, setPasteCampaign] = useState('');
  const [pasteDefaultStatus, setPasteDefaultStatus] = useState<LeadStatus>('unsent');
  const [pasteDataType, setPasteDataType] = useState<LeadDataType | ''>('');
  const [pasteDefaultCountry, setPasteDefaultCountry] = useState('');
  const [pasteDefaultSentDate, setPasteDefaultSentDate] = useState('');
  const [pasteUploadDate, setPasteUploadDate] = useState('');
  const [pasteMode, setPasteMode] = useState<'leads' | 'negative'>('leads');
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaDragActive, setMediaDragActive] = useState(false);
  const [mediaDefaultStatus, setMediaDefaultStatus] = useState<LeadStatus>('unsent');
  const [mediaDataType, setMediaDataType] = useState<LeadDataType | ''>('');
  const [mediaDefaultCountry, setMediaDefaultCountry] = useState('');
  const [mediaUploadDate, setMediaUploadDate] = useState('');
  const [mediaSourceSheetName, setMediaSourceSheetName] = useState('Extracted text');
  const [mediaRows, setMediaRows] = useState<ExtractedLeadDraft[]>([]);
  const [mediaWarnings, setMediaWarnings] = useState<string[]>([]);
  const [mediaEngine, setMediaEngine] = useState('');
  const [negativePasteText, setNegativePasteText] = useState('');
  const [negativePasteStatus, setNegativePasteStatus] = useState<'negative_reply' | 'junk' | 'hard_bounce'>('negative_reply');
  const [singleLeadOpen, setSingleLeadOpen] = useState(false);
  const [singleLead, setSingleLead] = useState<Partial<ExtractedLeadDraft> & { email: string }>({ email: '' });
  const [dedupeFile, setDedupeFile] = useState<File | null>(null);
  const [dedupeText, setDedupeText] = useState('');
  const [dedupeWindow, setDedupeWindow] = useState<number | 'all'>(3);
  const [statusFile, setStatusFile] = useState<File | null>(null);
  const [statusCreateMissing, setStatusCreateMissing] = useState(false);
  const [statusDefaultStatus, setStatusDefaultStatus] = useState<LeadStatus | ''>('');
  const [statusAllowFinalStatusOverwrite, setStatusAllowFinalStatusOverwrite] = useState(false);
  const [recoverableStatusChanges, setRecoverableStatusChanges] = useState<RecoverableStatusChange[] | null>(null);
  const [statusCampaign, setStatusCampaign] = useState('');
  const [statusDataType, setStatusDataType] = useState<LeadDataType | ''>('');
  const [statusDefaultCountry, setStatusDefaultCountry] = useState('');
  const [statusDefaultSentDate, setStatusDefaultSentDate] = useState('');
  const [statusUploadDate, setStatusUploadDate] = useState('');
  const [statusDateOrder, setStatusDateOrder] = useState<'auto' | 'dmy' | 'mdy'>('auto');
  const [statusDragActive, setStatusDragActive] = useState(false);
  const [pstDragActive, setPstDragActive] = useState(false);
  const [pstUploadDate, setPstUploadDate] = useState('');
  const [pstFallbackDate, setPstFallbackDate] = useState('');
  const [pstQueue, setPstQueue] = useState<PstQueueItem[]>([]);
  const [pstParserStatus, setPstParserStatus] = useState<{ installed: boolean; command: string | null; message?: string } | null>(null);
  const [newCampaignName, setNewCampaignName] = useState('');
  const [newCampaignDescription, setNewCampaignDescription] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserWorkspace, setNewUserWorkspace] = useState('');
  const [newUserRole, setNewUserRole] = useState<UserRole>('viewer');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [nextPassword, setNextPassword] = useState('');
  const [mailLogName, setMailLogName] = useState('');
  const [mailLogUrl, setMailLogUrl] = useState('');
  const [mailLogInterval, setMailLogInterval] = useState(30);
  const [mailLogLookback, setMailLogLookback] = useState(7);
  const [mailLogInsecureTls, setMailLogInsecureTls] = useState(false);
  const [editingMailLogSource, setEditingMailLogSource] = useState<MailLogSource | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [commandOpen, setCommandOpen] = useState(false);
  const [commandQuery, setCommandQuery] = useState('');
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  const setActiveView = (view: ActiveView) => {
    const targetPath = viewPaths[view];
    if (window.location.pathname !== targetPath) {
      window.history.pushState({ view }, '', targetPath);
    }
    setCurrentActiveView(view);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const canWrite = Boolean(user && ['admin', 'manager', 'uploader'].includes(user.role));
  const canAdmin = user?.role === 'admin';
  const canAudit = user?.role === 'admin';
  const maskSensitive = Boolean(user && ['viewer', 'uploader'].includes(user.role));
  const activeMeta = viewMeta[activeView];
  const userInitials = (user?.displayName ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
  const followUpAlertCount = followUpSummary.dueToday + followUpSummary.overdue;
  const activeFilterCount = [
    filters.q?.trim(), filters.emailContains?.trim(), filters.preset, filters.status, filters.dataType, filters.country?.trim(),
    filters.campaignId, filters.industry?.trim(), filters.industryMissing ? 'industryMissing' : '', filters.timeZone?.trim(), filters.provider, filters.emailType, filters.enrichment, filters.sourceContains?.trim(), filters.uploadDateFrom,
    filters.uploadDateTo, filters.sentDateFrom, filters.sentDateTo,
    filters.websiteUpdated, filters.websiteUpdatedFrom, filters.websiteUpdatedTo, filters.reviewOnly
  ].filter(Boolean).length;
  const sourceListOptions = {
    limit: 30,
    query: sourceQuery.trim() || undefined,
    kind: sourceKindFilter === 'all' ? undefined : sourceKindFilter,
    reply: sourceReplyFilter === 'all' ? undefined : sourceReplyFilter
  };
  sourceRequestScopeRef.current = JSON.stringify([user?.id, refreshKey, sourceListOptions]);

  function loadSourcePageCounts(pageRows: DataSource[]) {
    const scope = sourceRequestScopeRef.current;
    sourceCountsQueueRef.current = sourceCountsQueueRef.current.catch(() => undefined).then(async () => {
      if (scope !== sourceRequestScopeRef.current) return;
      let next = 0;
      await Promise.all([0, 1].map(async () => {
        while (next < pageRows.length && scope === sourceRequestScopeRef.current) {
          const source = pageRows[next++];
          const key = sourceKey(source);
          try {
            const summary = await getDataSourceSummary({ sourceJobId: source.sourceJobId ?? undefined,
              sourceFile: source.sourceJobId ? undefined : source.sourceFile,
              sourceUploadDate: source.sourceJobId ? undefined : source.uploadDate,
              sourceOwnerId: source.sourceOwnerId, sourceKind: source.kind });
            if (scope !== sourceRequestScopeRef.current) return;
            setDataSources(current => current.map(row => sourceKey(row) === key ? { ...row, ...summary,
              updatedAt: summary.updatedAt ?? row.updatedAt, countsPending: false, countsError: false } : row));
          } catch {
            if (scope !== sourceRequestScopeRef.current) return;
            setDataSources(current => current.map(row => sourceKey(row) === key && row.countsPending ? { ...row, countsError: true } : row));
          }
        }
      }));
    });
  }

  useEffect(() => {
    const handlePopState = () => {
      const nextView = viewFromPath(window.location.pathname);
      setCurrentActiveView(nextView);
      if (nextView === 'leads') {
        setFilters(leadFiltersFromUrl());
        setCurrentCursor(undefined);
        setCursorStack([]);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    function handleCommandShortcut(event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandOpen(true);
        return;
      }
      if (event.key === 'Escape') {
        setCommandOpen(false);
      }
    }
    window.addEventListener('keydown', handleCommandShortcut);
    return () => window.removeEventListener('keydown', handleCommandShortcut);
  }, []);

  useEffect(() => {
    if (activeView !== 'leads') return;
    const nextUrl = `${viewPaths.leads}${leadFiltersToSearch(filters)}`;
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    if (currentUrl !== nextUrl) {
      window.history.replaceState({ view: 'leads' }, '', nextUrl);
    }
  }, [activeView, filters]);

  useEffect(() => {
    if (!localStorage.getItem('email-data-platform-token')) {
      setAuthChecking(false);
      return;
    }

    getMe()
      .then((payload) => setUser(payload.user))
      .catch(() => setAuthToken(null))
      .finally(() => setAuthChecking(false));
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    let cancelled = false;
    const requestSeq = leadRequestSeq.current + 1;
    leadRequestSeq.current = requestSeq;
    setLoading(true);
    setLoadingMore(false);

    const controller = new AbortController();
    if (activeView !== 'leads') { setLoading(false); return () => controller.abort(); }
    const searchTimer = window.setTimeout(() => { void listLeads(filters, currentCursor, controller.signal)
      .then((page) => {
        if (cancelled || requestSeq !== leadRequestSeq.current) {
          return;
        }
        setLeads(page.rows);
        setNextCursor(page.nextCursor);
        setSelectedIds(new Set());
      })
      .catch((error) => { if (!cancelled) setNotice(error.message); })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      }); }, filters.q || filters.emailContains ? 300 : 0);

    return () => {
      cancelled = true;
      window.clearTimeout(searchTimer);
      controller.abort();
    };
  }, [filters, currentCursor, refreshKey, user, activeView]);

  useEffect(() => {
    if (!user || activeView !== 'leads') return;
    const controller = new AbortController();
    setLeadTotalCount(null);
    setLeadCountError(null);
    setAllMatchedSelected(false);
    const timer = window.setTimeout(() => {
      void countLeads(filters, controller.signal)
        .then((payload) => { if (!controller.signal.aborted) setLeadTotalCount(payload.totalCount); })
        .catch((error) => { if (!controller.signal.aborted) setLeadCountError(error.message); });
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [filters, refreshKey, user, activeView, leadCountRetry]);

  useEffect(() => {
    if (!user || activeView !== 'serverSync') return;
    let cancelled = false;
    let pending = false;
    const refreshSyncProgress = () => {
      if (pending || document.hidden) return;
      pending = true;
      void listMailLogSources()
        .then((payload) => { if (!cancelled) setMailLogSources(payload.rows); })
        .catch(() => undefined)
        .finally(() => { pending = false; });
    };
    refreshSyncProgress();
    const interval = window.setInterval(refreshSyncProgress, 5_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [activeView, user]);

  useEffect(() => {
    if (!user || activeView !== 'inboxSync') return;
    let cancelled = false;
    void getOutlookPstParserStatus()
      .then((payload) => { if (!cancelled) setPstParserStatus(payload); })
      .catch(() => { if (!cancelled) setPstParserStatus({ installed: false, command: null, message: 'Could not check PST parser status.' }); });
    return () => { cancelled = true; };
  }, [activeView, user, refreshKey]);

  useEffect(() => {
    if (activeView !== 'inboxSync' || busyAction === 'outlookPstQueue') return;
    const resumePaused = () => {
      if (pstQueue.some((item) => item.status === 'paused')) {
        void startPstQueueSync();
      }
    };
    window.addEventListener('online', resumePaused);
    return () => window.removeEventListener('online', resumePaused);
  }, [activeView, busyAction, pstQueue]);

  useEffect(() => {
    if (!user) {
      return;
    }

    let cancelled = false;
    setStatsRefreshing(true);
    getStats()
      .then((statsPayload) => {
        if (!cancelled) {
          setStats(statsPayload);
          setStatsUpdatedAt(new Date().toISOString());
        }
      })
      .catch((error) => {
        if (!cancelled) setNotice(error instanceof Error ? error.message : 'Dashboard totals could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setStatsRefreshing(false);
      });

    // Independent resources must not block each other when one endpoint fails.
    void listJobHistory().then((payload) => { if (!cancelled) setJobs(payload.rows); })
      .catch((error) => { if (!cancelled) setNotice(error.message); });
    void listCampaigns().then((payload) => { if (!cancelled) setCampaigns(payload.rows); })
      .catch((error) => { if (!cancelled) setNotice(error.message); });
    void listFollowUps().then((payload) => {
      if (!cancelled) { setFollowUps(payload.rows); setFollowUpSummary(payload.summary); }
    }).catch((error) => { if (!cancelled) setNotice(error.message); });
    return () => { cancelled = true; };
  }, [refreshKey, user, canAudit, canAdmin]);

  useEffect(() => {
    if (!user || !['imports', 'inboxSync'].includes(activeView)) return;
    let cancelled = false;
    void listStatusUpdateBatches().then((payload) => { if (!cancelled) setStatusBatches(payload.rows); })
      .catch((error) => { if (!cancelled) setNotice(error.message); });
    return () => { cancelled = true; };
  }, [user, activeView, refreshKey]);

  useEffect(() => {
    if (!user || activeView !== 'analytics') return;
    let cancelled = false;
    void getAnalytics()
      .then((payload) => { if (!cancelled) setAnalytics(payload); })
      .catch((error) => { if (!cancelled) setNotice(error instanceof Error ? error.message : 'Analytics could not be loaded.'); });
    return () => { cancelled = true; };
  }, [activeView, refreshKey, user]);

  useEffect(() => {
    if (!user || !canAdmin || activeView !== 'suppression') return;
    let cancelled = false;
    void listSuppression()
      .then((payload) => { if (!cancelled) setSuppression(payload.rows); })
      .catch((error) => { if (!cancelled) setNotice(error instanceof Error ? error.message : 'Suppression list could not be loaded.'); });
    return () => { cancelled = true; };
  }, [activeView, refreshKey, user, canAdmin]);

  useEffect(() => {
    if (!user || !canAudit || activeView !== 'audit') return;
    let cancelled = false;
    void listAuditLogs()
      .then((payload) => { if (!cancelled) setAuditLogs(payload.rows); })
      .catch((error) => { if (!cancelled) setNotice(error instanceof Error ? error.message : 'Audit logs could not be loaded.'); });
    return () => { cancelled = true; };
  }, [activeView, refreshKey, user, canAudit]);

  useEffect(() => {
    if (!user || !canAdmin || activeView !== 'settings') return;
    let cancelled = false;
    void listUsers()
      .then((payload) => { if (!cancelled) setUsers(payload.rows); })
      .catch((error) => { if (!cancelled) setNotice(error instanceof Error ? error.message : 'Users could not be loaded.'); });
    return () => { cancelled = true; };
  }, [activeView, refreshKey, user, canAdmin]);

  useEffect(() => {
    if (!user || !canAdmin || activeView !== 'performance') return;
    let cancelled = false;
    void getPerformanceSnapshot()
      .then((payload) => { if (!cancelled) setPerformance(payload); })
      .catch((error) => { if (!cancelled) setNotice(error instanceof Error ? error.message : 'Performance metrics could not be loaded.'); });
    return () => { cancelled = true; };
  }, [activeView, refreshKey, user, canAdmin]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setSourceCatalogLoading(true);
      setSourceCatalogError('');
      setDataSources([]);
      setSourceNextCursor(null);
      sourceCountsQueueRef.current = Promise.resolve();
      listDataSources({ ...sourceListOptions, includeCounts: false })
        .then((payload) => {
          if (cancelled) return;
          sourceLoadedScopeRef.current = sourceRequestScopeRef.current;
          setDataSources(payload.rows);
          setSourceNextCursor(payload.nextCursor);
          loadSourcePageCounts(payload.rows);
          setActiveCatalogSourceKey(null);
          sourceDirectoryActiveKeyRef.current = null;
        })
        .catch((error) => {
          if (!cancelled) setSourceCatalogError(error instanceof Error ? error.message : 'Data sources could not be loaded.');
        })
        .finally(() => {
          if (!cancelled) setSourceCatalogLoading(false);
        });
    }, sourceQuery.trim() ? 250 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [user, sourceQuery, sourceKindFilter, sourceReplyFilter, refreshKey]);

  useEffect(() => {
    const activeJobs = jobs.filter((job) => job.status === 'queued' || job.status === 'running');
    if (activeJobs.length === 0) {
      return;
    }

    let cancelled = false;
    let pending = false;
    const timer = window.setInterval(() => {
      if (pending || document.hidden) return;
      pending = true;
      void getJobsBatch(activeJobs.slice(0, 100).map(job => job.id)).then(({ rows: updatedJobs }) => {
        if (cancelled) return;
        setJobs((existing) =>
          existing.map((job) => updatedJobs.find((updated) => updated.id === job.id) ?? job)
        );
        setActiveJob((current) => updatedJobs.find((updated) => updated.id === current?.id) ?? current);

        if (updatedJobs.some((job) => job.status === 'completed' || job.status === 'failed')) {
          setRefreshKey((value) => value + 1);
        }
      }).catch(() => undefined).finally(() => { pending = false; });
    }, 10000);

    return () => { cancelled = true; window.clearInterval(timer); };
  }, [jobs]);

  useEffect(() => {
    if (!nextCursor || leads.length >= 5000 || loading || loadingMore || !loadMoreSentinelRef.current) {
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        loadMoreRef.current();
      }
    }, { rootMargin: '600px 0px' });
    observer.observe(loadMoreSentinelRef.current);
    return () => observer.disconnect();
  }, [nextCursor, loading, loadingMore, leads.length]);

  const displayedLeadTotal = leadTotalCount ?? leads.length;
  const hasExactLeadTotal = leadTotalCount !== null;
  const selectedCount = allMatchedSelected ? displayedLeadTotal : selectedIds.size;
  const selectedSource = dataSources.find((source) => (
    source.sourceJobId === filters.sourceJobId
    || (!filters.sourceJobId && source.sourceFile === filters.sourceFile && source.uploadDate === filters.sourceUploadDate
      && (!filters.sourceOwnerId || source.sourceOwnerId === filters.sourceOwnerId)
      && (!filters.sourceKind || source.kind === filters.sourceKind))
  ));
  const sourceKey = (source: DataSource) => source.sourceJobId ?? `${source.sourceOwnerId}|${source.sourceFile}|${source.uploadDate}|${source.kind}`;
  const visibleSources = useMemo(() => {
    return dataSources;
  }, [dataSources]);
  useEffect(() => {
    if (sourceDirectoryListRef.current) sourceDirectoryListRef.current.scrollTop = 0;
  }, [sourceQuery, sourceKindFilter, sourceReplyFilter]);

  const activeCatalogSource = visibleSources.find((source) => sourceKey(source) === activeCatalogSourceKey) ?? visibleSources[0] ?? null;
  const activeCatalogSourceSummary = activeCatalogSource && activeSourceSummary?.key === sourceKey(activeCatalogSource)
    ? activeSourceSummary.summary
    : null;
  const activeCatalogSourceOutcomes = activeCatalogSourceSummary
    ? { ...activeCatalogSource!, ...activeCatalogSourceSummary, updatedAt: activeCatalogSourceSummary.updatedAt ?? activeCatalogSource!.updatedAt }
    : null;
  const loadedSourceLeadCount = dataSources.reduce((total, source) => total + source.leadCount, 0);
  const showBulkToolbar = canWrite && (selectedCount > 0 || allMatchedSelected || hasFilteredBulkScope(filters));
  const bulkScopeLabel = allMatchedSelected
    ? hasExactLeadTotal ? `${formatNumber(selectedCount)} matched selected` : 'All matching records selected (count pending)'
    : selectedCount > 0
      ? `${formatNumber(selectedCount)} selected`
      : hasExactLeadTotal
        ? `Filtered scope: ${formatNumber(displayedLeadTotal)} records`
        : 'Filtered scope';

  useEffect(() => {
    if (!user || activeView !== 'sources' || !activeCatalogSource) return;
    let cancelled = false;
    const key = sourceKey(activeCatalogSource);
    setActiveSourceSummaryLoading(true);
    setActiveSourceSummary(null);
    setActiveSourceSummaryError(null);
    const summaryTimer = window.setTimeout(() => { void getDataSourceSummary({
      sourceJobId: activeCatalogSource.sourceJobId ?? undefined,
      sourceFile: activeCatalogSource.sourceJobId ? undefined : activeCatalogSource.sourceFile,
      sourceUploadDate: activeCatalogSource.sourceJobId ? undefined : activeCatalogSource.uploadDate,
      sourceOwnerId: activeCatalogSource.sourceOwnerId,
      sourceKind: activeCatalogSource.kind
    })
      .then((summary) => {
        if (!cancelled) {
          setActiveSourceSummary({ key, summary });
          setDataSources(current => current.map(row => sourceKey(row) === key ? { ...row, ...summary, updatedAt: summary.updatedAt ?? row.updatedAt, countsPending: false, countsError: false } : row));
        }
      })
      .catch((error) => {
        if (!cancelled) setActiveSourceSummaryError(error instanceof Error ? error.message : 'Could not load live source outcomes.');
      })
      .finally(() => {
        if (!cancelled) setActiveSourceSummaryLoading(false);
      }); }, 180);
    return () => { cancelled = true; window.clearTimeout(summaryTimer); };
  }, [activeCatalogSource?.sourceJobId, activeCatalogSource?.sourceFile, activeCatalogSource?.uploadDate, activeCatalogSource?.sourceOwnerId, activeView, refreshKey, sourceSummaryRevision, user]);

  const topStats = useMemo(
    () => [
      {
        label: 'Total leads',
        value: stats?.total,
        icon: Database
      },
      {
        label: 'Fresh unsent',
        value: stats?.byDeliveryStatus.not_sent ?? stats?.byStatus.unsent,
        icon: MailCheck
      },
      {
        label: 'Sent / relayed',
        value: stats?.byDeliveryStatus.sent ?? stats?.byStatus.sent,
        icon: CheckCircle2
      },
      {
        label: 'Failed delivery',
        value: stats?.byDeliveryStatus.failed ?? stats?.byStatus.failed,
        icon: AlertTriangle
      },
      {
        label: 'Suppressed',
        value: stats?.bySuppressionStatus.suppressed ?? stats?.suppressed,
        icon: ShieldCheck
      },
      {
        label: 'Positive replies',
        value: stats?.byReplyStatus.positive ?? stats?.byStatus.positive_reply,
        icon: CheckCircle2
      },
      {
        label: 'Negative replies',
        value: stats?.byReplyStatus.negative ?? stats?.byStatus.negative_reply,
        icon: AlertTriangle
      }
    ],
    [stats]
  );

  const maxDailyLeads = Math.max(
    1,
    ...(analytics?.dailyLeadActivity.map((row) => row.createdCount) ?? [0])
  );

  function setFilterValue<K extends keyof LeadFilters>(key: K, value: LeadFilters[K]) {
    setFilters((current) => ({ ...current, [key]: value }));
    setCurrentCursor(undefined);
    setCursorStack([]);
    setAllMatchedSelected(false);
  }

  function setLeadSort(sortBy: NonNullable<LeadFilters['sortBy']>) {
    setFilters((current) => ({
      ...current,
      sortBy,
      sortDir: current.sortBy === sortBy && current.sortDir === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentCursor(undefined);
    setCursorStack([]);
    setAllMatchedSelected(false);
  }

  function toggleVisibleColumn(column: LeadTableColumn) {
    setVisibleColumns((current) => {
      const next = new Set(current);
      if (next.has(column)) next.delete(column);
      else next.add(column);
      return next;
    });
  }

  function clearAllLeadFilters() {
    setFilters({ limit: filters.limit ?? 100, preset: '' });
    setCurrentCursor(undefined);
    setCursorStack([]);
    setAllMatchedSelected(false);
  }

  function openSource(source: DataSource, sheet?: string) {
    setFilters({
      limit: 250,
      preset: '',
      sourceJobId: source.sourceJobId ?? undefined,
      sourceSheet: sheet,
      sourceFile: source.sourceJobId ? undefined : source.sourceFile,
      sourceUploadDate: source.sourceJobId ? undefined : source.uploadDate,
      sourceOwnerId: source.sourceOwnerId,
      sourceKind: source.kind
    });
    setCurrentCursor(undefined);
    setCursorStack([]);
    setAllMatchedSelected(false);
    setActiveView('leads');
  }

  async function loadOlderSources() {
    if (!sourceNextCursor || sourceLoadingMoreRef.current) return;
    if (sourceLoadedScopeRef.current !== sourceRequestScopeRef.current) return;
    const requestScope = sourceRequestScopeRef.current;
    sourceLoadingMoreRef.current = true;
    setSourceLoadingMore(true);
    try {
      const options = { ...sourceListOptions, cursor: sourceNextCursor };
      const payload = await listDataSources({ ...options, includeCounts: false });
      if (requestScope !== sourceRequestScopeRef.current) return;
      setDataSources((current) => {
        const seen = new Set(current.map(sourceKey));
        return [...current, ...payload.rows.filter((source) => !seen.has(sourceKey(source)))];
      });
      setSourceNextCursor(payload.nextCursor);
      loadSourcePageCounts(payload.rows);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Could not load older sources.');
    } finally {
      sourceLoadingMoreRef.current = false;
      setSourceLoadingMore(false);
    }
  }

  useEffect(() => {
    if (activeView !== 'sources' || !sourceNextCursor || sourceLoadingMore || !sourceMoreRef.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) void loadOlderSources();
    }, { root: sourceDirectoryListRef.current, rootMargin: '260px' });
    observer.observe(sourceMoreRef.current);
    return () => observer.disconnect();
  }, [activeView, sourceNextCursor, sourceLoadingMore, sourceListOptions]);

  function handleSourceDirectoryScroll(event: React.UIEvent<HTMLDivElement>) {
    const directory = event.currentTarget;
    if (directory.scrollHeight - directory.scrollTop - directory.clientHeight < 260) {
      void loadOlderSources();
    }

    const directoryTop = directory.getBoundingClientRect().top;
    const visibleRow = [...directory.querySelectorAll<HTMLButtonElement>('[data-source-key]')]
      .find((row) => row.getBoundingClientRect().bottom > directoryTop + 18);
    const key = visibleRow?.dataset.sourceKey ?? null;
    if (key && key !== sourceDirectoryActiveKeyRef.current) {
      sourceDirectoryActiveKeyRef.current = key;
      setActiveCatalogSourceKey(key);
    }
  }

  function clearSourceFilter() {
    setFilters((current) => ({
      ...current,
      sourceJobId: undefined,
      sourceSheet: undefined,
      sourceFile: undefined,
      sourceUploadDate: undefined
    }));
    setCurrentCursor(undefined);
    setCursorStack([]);
  }

  async function saveFollowUpPlan(sentDate: string, gapDays: number) {
    if (!followUpSource) {
      return;
    }
    setBusyAction('followUpSchedule');
    setNotice('');
    try {
      await scheduleFollowUps({
        sourceJobId: followUpSource.sourceJobId ?? undefined,
        sourceFile: followUpSource.sourceJobId ? undefined : followUpSource.sourceFile,
        sourceUploadDate: followUpSource.sourceJobId ? undefined : followUpSource.uploadDate,
        sentDate,
        gapDays
      });
      setFollowUpSource(null);
      setNotice('Two follow-ups have been scheduled for this data source.');
      setRefreshKey((value) => value + 1);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Follow-up schedule could not be saved.');
    } finally {
      setBusyAction(null);
    }
  }

  async function handleSourceEnrichment(source: DataSource) {
    setBusyAction(`enrich:${source.sourceJobId ?? source.sourceFile}:${source.uploadDate}`);
    setNotice('');
    try {
      const job = await enrichDataSource({
        sourceJobId: source.sourceJobId ?? undefined,
        sourceFile: source.sourceJobId ? undefined : source.sourceFile,
        sourceUploadDate: source.sourceJobId ? undefined : source.uploadDate,
        sourceOwnerId: source.sourceOwnerId,
        sourceKind: source.kind
      });
      setJobs((current) => [job, ...current.filter((item) => item.id !== job.id)].slice(0, 10));
      setNotice(`Website enrichment started for ${source.sourceFile}. Existing saved details will be preserved.`);
      setRefreshKey((value) => value + 1);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Source enrichment could not be started.');
    } finally {
      setBusyAction(null);
    }
  }

  async function handleWorkspaceMissingEnrichment() {
    setBusyAction('enrich:workspace-missing');
    setNotice('');
    try {
      const job = await enrichWorkspaceMissingDetails();
      setJobs((current) => [job, ...current.filter((item) => item.id !== job.id)].slice(0, 10));
      setNotice(`Missing-details enrichment started for ${formatNumber(Number(job.options?.total ?? job.totalRows))} leads. Existing profile details will be skipped.`);
      setRefreshKey((value) => value + 1);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Missing-details enrichment could not be started.');
    } finally {
      setBusyAction(null);
    }
  }

  async function handleRetailIndustryVerification() {
    setBusyAction('enrich:retail-industry');
    setNotice('');
    try {
      const job = await verifyRetailIndustries();
      setJobs((current) => [job, ...current.filter((item) => item.id !== job.id)].slice(0, 10));
      setNotice(`Retail industry verification started for ${formatNumber(Number(job.options?.total ?? job.totalRows))} leads. Only high-confidence non-Retail website evidence can replace Retail.`);
      setRefreshKey((value) => value + 1);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Retail industry verification could not be started.');
    } finally {
      setBusyAction(null);
    }
  }

  async function changeFollowUpStatus(id: string, status: FollowUpTaskStatus) {
    setBusyAction(`followUp:${id}`);
    try {
      await updateFollowUpStatus(id, status);
      setRefreshKey((value) => value + 1);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Follow-up update failed.');
    } finally {
      setBusyAction(null);
    }
  }

  function toggleSelected(id: string) {
    setAllMatchedSelected(false);
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleAllSelected() {
    if (allMatchedSelected) {
      setAllMatchedSelected(false);
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds((current) => {
      const allSelected = leads.length > 0 && leads.every((lead) => current.has(lead.id));
      return allSelected ? new Set() : new Set(leads.map((lead) => lead.id));
    });
  }

  async function safeDownload(path: string, fallbackName: string) {
    setNotice(path.startsWith('/api/exports/leads') ? 'Preparing export in the background. The job is saved in Recent jobs.' : '');
    try {
      await downloadFile(path, fallbackName);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Download failed.');
    }
  }

  async function handleQueueExport() {
    setBusyAction('exportQueue');
    setNotice('');
    try {
      const job = await queueLeadExport(filters);
      setJobs((current) => [job, ...current.filter((item) => item.id !== job.id)].slice(0, 20));
      setActiveView('imports');
      setNotice('Export job started. It will appear in Recent jobs with CSV and XLSX download links when ready.');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Export job could not be started.');
    } finally {
      setBusyAction(null);
    }
  }

  async function rollbackJob(job: Job) {
    if (!window.confirm('Rollback this bulk field update? It will restore the saved values from before the job started.')) {
      return;
    }
    setBusyAction(`rollback:${job.id}`);
    setNotice('');
    try {
      const result = await rollbackBulkJob(job.id);
      const latest = await getJob(job.id);
      setJobs((current) => current.map((item) => (item.id === latest.id ? latest : item)));
      setActiveJob(latest);
      setNotice(result.available
        ? `Rollback restored ${formatNumber(result.restored)} lead records.`
        : 'Rollback is not available for this job anymore.');
      setRefreshKey((value) => value + 1);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Rollback failed.');
    } finally {
      setBusyAction(null);
    }
  }

  async function runEnrichmentJobAction(job: Job, action: 'pause' | 'resume' | 'retry') {
    setBusyAction(`enrichment:${action}:${job.id}`);
    setNotice('');
    try {
      const result = action === 'pause'
        ? await pauseEnrichmentJob(job.id)
        : action === 'resume'
          ? await resumeEnrichmentJob(job.id)
          : await retryFailedEnrichmentJob(job.id);
      if ('id' in result) {
        setJobs((current) => [result, ...current.filter((item) => item.id !== result.id)].slice(0, 30));
        setActiveJob(result);
        setNotice('Retry failed-only enrichment job started.');
      } else {
        const latest = await getJob(job.id);
        setJobs((current) => current.map((item) => (item.id === latest.id ? latest : item)));
        setActiveJob(latest);
        setNotice(action === 'pause' ? 'Enrichment pause requested.' : 'Enrichment resume started.');
      }
      setRefreshKey((value) => value + 1);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Enrichment action failed.');
    } finally {
      setBusyAction(null);
    }
  }

  async function openJobDetails(job: Job) {
    setActiveJob(job);
    try {
      const latest = await getJob(job.id);
      setJobs((current) => current.map((item) => (item.id === latest.id ? latest : item)));
      setActiveJob(latest);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Could not load job details.');
    }
  }

  async function openStatusBatchReport(batch: StatusUpdateBatch) {
    try {
      setActiveStatusBatch(await getStatusUpdateBatch(batch.id));
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Could not load the status update report.');
    }
  }

  async function handleRowStatusChange(id: string, status: LeadStatus) {
    if (!canWrite) {
      setNotice('You do not have permission to change statuses.');
      return;
    }

    setNotice('');
    await updateLeadStatus(id, status);
    setRefreshKey((value) => value + 1);
  }

  async function handleLeadSave(payload: LeadDetailsUpdate) {
    if (!activeLead || !canWrite) {
      return;
    }

    setBusyAction('leadDetails');
    setNotice('');
    try {
      const updatedLead = await updateLeadDetails(activeLead.id, payload);
      setLeads((current) =>
        current.map((lead) => (lead.id === updatedLead.id ? updatedLead : lead))
      );
      setActiveLead(null);
      setNotice(`Lead details updated for ${updatedLead.email}.`);
      setRefreshKey((value) => value + 1);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Lead details update failed.');
    } finally {
      setBusyAction(null);
    }
  }

  function buildBulkFieldUpdate(): BulkLeadFieldUpdate | null {
    if (bulkUpdateTarget === 'status') {
      return { status: bulkStatusValue };
    }
    if (bulkUpdateTarget === 'industry') {
      if (!bulkIndustryValue.trim()) {
        setNotice('Enter an industry value before applying bulk update.');
        return null;
      }
      return { industry: bulkIndustryValue.trim() };
    }
    if (bulkUpdateTarget === 'dataType') {
      return { dataType: bulkDataTypeValue };
    }
    if (bulkUpdateTarget === 'campaignId') {
      return { campaignId: bulkCampaignValue || null };
    }
    if (!bulkTagValue.trim()) {
      setNotice('Enter a tag before applying bulk update.');
      return null;
    }
    return { appendTag: bulkTagValue.trim() };
  }

  function bulkUpdateLabel(): string {
    if (bulkUpdateTarget === 'status') return statusLabels[bulkStatusValue];
    if (bulkUpdateTarget === 'industry') return bulkIndustryValue.trim() || 'industry';
    if (bulkUpdateTarget === 'dataType') return dataTypeLabels[bulkDataTypeValue];
    if (bulkUpdateTarget === 'campaignId') return bulkCampaignValue || 'Unassigned';
    return bulkTagValue.trim() || 'tag';
  }

  async function confirmBulkPreview(updates: BulkLeadFieldUpdate, ids?: string[]) {
    setBusyAction('bulkPreview');
    try {
      const preview = await request<{ matched: number; eligible: number; protected: number }>('/api/leads/bulk-preview', { method: 'POST', body: JSON.stringify({ ids, filters: ids ? undefined : filters, updates }) });
      return preview.eligible > 0 && window.confirm(`Apply "${bulkUpdateLabel()}"?\nMatched: ${formatNumber(preview.matched)}\nEligible: ${formatNumber(preview.eligible)}\nProtected / skipped: ${formatNumber(preview.protected)}\nCounts are a preview; records may change before processing.`);
    } catch (error) { setNotice(error instanceof Error ? error.message : 'Bulk preview failed.'); return false; }
    finally { setBusyAction(null); }
  }

  async function handleBulkStatus() {
    if (!canWrite) {
      setNotice('You do not have permission to change lead fields.');
      return;
    }

    if (!allMatchedSelected && selectedIds.size === 0) {
      setNotice('Select at least one lead before applying a bulk update.');
      return;
    }

    const updates = buildBulkFieldUpdate();
    if (!updates) return;
    if (!await confirmBulkPreview(updates, allMatchedSelected ? undefined : [...selectedIds])) return;

    setBusyAction('bulkStatus');
    try {
      const result = allMatchedSelected
        ? await bulkUpdateFilteredFields(filters, updates)
        : await bulkUpdateFields([...selectedIds], updates);
      if ('queued' in result) {
        setJobs((current) => [result.job, ...current.filter((item) => item.id !== result.job.id)].slice(0, 20));
        setNotice(`Bulk update queued for ${formatNumber(result.targetCount)} matched lead records. Track it in Recent jobs.`);
      } else {
        if (result.job) {
          setJobs((current) => [result.job!, ...current.filter((item) => item.id !== result.job!.id)].slice(0, 20));
        }
        setNotice(`Updated ${formatNumber(result.updated)} lead records. Rollback snapshot is saved in Recent jobs for one hour.`);
      }
      setAllMatchedSelected(false);
      setRefreshKey((value) => value + 1);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Bulk field update failed.');
    } finally {
      setBusyAction(null);
    }
  }

  async function handleFilteredStatus() {
    if (!canWrite) {
      setNotice('You do not have permission to change lead fields.');
      return;
    }

    if (!hasFilteredBulkScope(filters)) {
      setNotice('Add upload date, campaign, data type, country, industry, email search, or sent-date filter first.');
      return;
    }

    const updates = buildBulkFieldUpdate();
    if (!updates) return;
    if (!await confirmBulkPreview(updates)) return;

    setBusyAction('filteredStatus');
    try {
      const result = await bulkUpdateFilteredFields(filters, updates);
      if ('queued' in result) {
        setJobs((current) => [result.job, ...current.filter((item) => item.id !== result.job.id)].slice(0, 20));
        setNotice(`Bulk update queued for ${formatNumber(result.targetCount)} filtered lead records. Track it in Recent jobs.`);
      } else {
        if (result.job) {
          setJobs((current) => [result.job!, ...current.filter((item) => item.id !== result.job!.id)].slice(0, 20));
        }
        setNotice(`Updated ${formatNumber(result.updated)} filtered lead records. Rollback snapshot is saved in Recent jobs for one hour.`);
      }
      setRefreshKey((value) => value + 1);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Filtered field update failed.');
    } finally {
      setBusyAction(null);
    }
  }

  function chooseLeadFiles(files: File[]) {
    if (busyAction === 'leadImport') return;
    const valid = files.filter(file => /\.(csv|txt|xlsx|xlsm)$/i.test(file.name));
    if (valid.length !== files.length) setNotice('Only CSV, TXT, XLSX and XLSM files are supported.');
    setLeadFiles(current => {
      const next = [...current];
      for (const file of valid) {
        if (!next.some(item => item.file.name === file.name && item.file.size === file.size && item.file.lastModified === file.lastModified)) {
          next.push({ file, plans: null, status: 'ready' });
        }
      }
      return next;
    });
    if (valid.length) setLeadPasteText('');
  }

  async function handleLeadImport() {
    if (!canWrite || leadImportRunningRef.current) return;
    setLeadImportError('');
    const revealProblem = (file?: File) => window.requestAnimationFrame(() => {
      const root = file ? leadFileSectionsRef.current.get(file) : leadImportPanelRef.current;
      const target = root?.querySelector<HTMLElement>('[data-import-problem]') ?? root;
      if (!target) return;
      let parent = target.parentElement;
      while (parent && parent !== root) {
        if (parent instanceof HTMLDetailsElement) parent.open = true;
        parent = parent.parentElement;
      }
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.focus({ preventScroll: true });
    });
    const pending = leadFiles.filter(item => item.status !== 'submitted');
    const items = leadFiles.length ? pending : leadPasteText.trim() ? [{ file: pastedTableToFile(leadPasteText), plans: null }] : [];
    if (!items.length) {
      setLeadImportError(leadFiles.length ? 'All selected files have already been submitted. Choose another file to import.' : 'Choose a CSV/XLSX file or paste spreadsheet rows first.');
      revealProblem();
      return;
    }
    const invalidFile = items.find(item => /\.(xlsx|xlsm)$/i.test(item.file.name) && !item.plans);
    if (invalidFile) {
      revealProblem(invalidFile.file);
      return;
    }

    leadImportRunningRef.current = true;
    setBusyAction('leadImport');
    let submitted = 0;
    let firstFailed: File | undefined;
    try {
      for (const item of items) {
      setLeadFiles(current => current.map(entry => entry.file === item.file ? { ...entry, status: 'uploading', error: undefined } : entry));
      try {
      const job = await uploadLeadImport(item.file, {
        defaultStatus: leadDefaultStatus,
        campaignId: leadCampaign.trim() || undefined,
        dataType: leadDataType || undefined,
        defaultCountry: leadDefaultCountry.trim() || undefined,
        defaultSentDate: leadDefaultSentDate || undefined,
        uploadDate: leadUploadDate || undefined,
        sourceSheetName: leadFiles.length ? undefined : 'Pasted rows',
        sheetPlans: item.plans ?? undefined
      });
      setJobs((current) => [job, ...current.filter((item) => item.id !== job.id)].slice(0, 10));
      submitted++;
      setLeadFiles(current => current.map(entry => entry.file === item.file ? { ...entry, status: 'submitted' } : entry));
      } catch (error) {
        firstFailed ??= item.file;
        setLeadFiles(current => current.map(entry => entry.file === item.file ? { ...entry, status: 'failed', error: error instanceof Error ? error.message : 'Upload failed' } : entry));
        if (!leadFiles.length) setLeadImportError(error instanceof Error ? error.message : 'Upload failed. Please retry.');
      }
      }
      setNotice(`${submitted} of ${items.length} files submitted for background import. ${items.length - submitted} uploads failed.`);
      if (submitted && !leadFiles.length) setLeadPasteText('');
      if (firstFailed) revealProblem(leadFiles.length ? firstFailed : undefined);
    } finally {
      leadImportRunningRef.current = false;
      setBusyAction(null);
    }
  }

  async function handlePasteImport() {
    if (!pasteText.trim()) {
      setNotice('Paste at least one lead block first.');
      return;
    }

    setBusyAction('pasteImport');
    try {
      const result = await pasteLeads({
        text: pasteText,
        defaultStatus: pasteDefaultStatus,
        campaignId: pasteCampaign.trim() || undefined,
        dataType: pasteDataType || undefined,
        defaultCountry: pasteDefaultCountry.trim() || undefined,
        defaultSentDate: pasteDefaultSentDate || undefined,
        uploadDate: pasteUploadDate || undefined
      });
      setPasteText('');
      setFilters({ limit: 50, preset: '', q: result.firstEmail || undefined });
      setCurrentCursor(undefined);
      setCursorStack([]);
      setNotice(
        `Pasted leads saved: ${formatNumber(result.insertedRows)} added, ${formatNumber(result.updatedRows)} updated, ${formatNumber(result.invalidRows + result.duplicateRows)} skipped.`
      );
      setRefreshKey((value) => value + 1);
      setActiveView('leads');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Paste import failed.');
    } finally {
      setBusyAction(null);
    }
  }

  async function handleMediaPreview() {
    if (!mediaFile) {
      setNotice('Choose a PDF, document, screenshot, or image first.');
      return;
    }
    setBusyAction('mediaPreview');
    setNotice('');
    try {
      const preview = await previewMediaExtraction(mediaFile, {
        defaultStatus: mediaDefaultStatus,
        dataType: mediaDataType || undefined,
        defaultCountry: mediaDefaultCountry.trim() || undefined,
        uploadDate: mediaUploadDate || undefined,
        sourceSheetName: mediaSourceSheetName.trim() || undefined
      });
      setMediaRows(preview.rows);
      setMediaWarnings(preview.warnings ?? []);
      setMediaEngine(preview.engine);
      setNotice(preview.totalRows > 0
        ? `Detected ${formatNumber(preview.totalRows)} lead record${preview.totalRows === 1 ? '' : 's'} from ${mediaFile.name}. Review before saving.`
        : 'No lead rows were detected. Try a text-based PDF/document or paste the extracted text in Paste Leads.');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Media extraction failed.');
    } finally {
      setBusyAction(null);
    }
  }

  function updateMediaRow(index: number, field: keyof ExtractedLeadDraft, value: string) {
    setMediaRows((current) => current.map((row, rowIndex) => (
      rowIndex === index ? { ...row, [field]: value } : row
    )));
  }

  async function handleMediaImport() {
    if (mediaRows.length === 0) {
      setNotice('Extract and review at least one row first.');
      return;
    }
    setBusyAction('mediaImport');
    setNotice('');
    try {
      const payload = {
        rows: mediaRows,
        sourceFileName: mediaFile?.name,
        sourceSheetName: mediaSourceSheetName.trim() || undefined,
        defaultStatus: mediaDefaultStatus,
        dataType: mediaDataType || undefined,
        defaultCountry: mediaDefaultCountry.trim() || undefined,
        uploadDate: mediaUploadDate || undefined
      };
      const result = await importExtractedLeads(payload);
      setMediaRows([]);
      setMediaFile(null);
      setRefreshKey((value) => value + 1);
      setNotice(`Extracted leads saved: ${formatNumber(result.insertedRows)} added, ${formatNumber(result.updatedRows)} updated.`);
      setFilters({ limit: 100, preset: '', q: result.firstEmail || undefined });
      setActiveView('leads');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Extracted lead import failed.');
    } finally {
      setBusyAction(null);
    }
  }

  async function handleNegativePasteImport() {
    if (!negativePasteText.trim()) {
      setNotice('Paste negative, junk, or bounce emails first.');
      return;
    }
    setBusyAction('negativePaste');
    setNotice('');
    try {
      const result = await pasteSuppressionLeads({
        text: negativePasteText,
        status: negativePasteStatus
      });
      setNegativePasteText('');
      setRefreshKey((value) => value + 1);
      setNotice(`Suppression paste saved: ${formatNumber(result.insertedRows)} added, ${formatNumber(result.updatedRows)} updated, ${formatNumber(result.suppressedRows)} protected.`);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Suppression paste failed.');
    } finally {
      setBusyAction(null);
    }
  }

  async function handleSingleLeadSave(event: React.FormEvent) {
    event.preventDefault();
    if (!singleLead.email.trim()) {
      setNotice('Email is required for a single lead.');
      return;
    }
    setBusyAction('singleLead');
    setNotice('');
    try {
      const result = await createSingleLead({
        lead: singleLead,
        defaultStatus: singleLead.status ?? 'unsent',
        dataType: singleLead.dataType ?? undefined,
        defaultCountry: singleLead.country ?? undefined,
        uploadDate: singleLead.uploadDate ?? todayForInput(),
        sourceFileName: 'Manual single lead'
      });
      setSingleLead({ email: '' });
      setSingleLeadOpen(false);
      setRefreshKey((value) => value + 1);
      setFilters({ limit: 50, preset: '', q: result.firstEmail || singleLead.email });
      setNotice('Single lead saved.');
      setActiveView('leads');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Single lead save failed.');
    } finally {
      setBusyAction(null);
    }
  }

  async function handleDeduplication() {
    const pasted = dedupeFile ? null : (dedupeText.trim() ? pastedTextToFile(dedupeText) : null);
    const file = dedupeFile ?? pasted?.file ?? null;
    if (!file) {
      setNotice('Choose a CSV file or paste email rows first.');
      return;
    }
    if (pasted && pasted.emailCount === 0) {
      setNotice('No valid email IDs were found in the pasted text.');
      return;
    }
    if (pasted && pasted.emailCount > 500_000) {
      setNotice(`Pasted ${formatNumber(pasted.emailCount)} emails. This may take longer, but the clean-list job has been queued.`);
    }

    setBusyAction('dedupe');
    try {
      const job = await uploadDeduplication(file, { windowMonths: dedupeWindow });
      setJobs((current) => [job, ...current.filter((item) => item.id !== job.id)].slice(0, 10));
      setNotice(pasted ? `Deduplication is running for ${formatNumber(pasted.emailCount)} pasted emails.` : 'Deduplication is running.');
      setDedupeFile(null);
      setDedupeText('');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Deduplication failed.');
    } finally {
      setBusyAction(null);
    }
  }

  async function handleStatusCsv() {
    if (!statusFile) {
      setNotice('Choose a CSV/XLSX delivery or status file first.');
      return;
    }

    setBusyAction('statusCsv');
    try {
      const job = await uploadStatusCsv(statusFile, {
        createMissing: statusCreateMissing,
        defaultStatus: statusDefaultStatus || undefined,
        allowFinalStatusOverwrite: statusAllowFinalStatusOverwrite,
        campaignId: statusCampaign.trim() || undefined,
        dataType: statusDataType || undefined,
        defaultCountry: statusDefaultCountry.trim() || undefined,
        defaultSentDate: statusDefaultSentDate || undefined,
        uploadDate: statusUploadDate || undefined,
        dateOrder: statusDateOrder
      });
      setJobs((current) => [job, ...current.filter((item) => item.id !== job.id)].slice(0, 10));
      setStatusBatches((current) => [{
        id: job.id,
        fileName: job.fileName,
        status: 'queued' as const,
        totalRows: 0,
        validRows: 0,
        invalidRows: 0,
        duplicateRows: 0,
        insertedRows: 0,
        updatedRows: 0,
        protectedRows: 0,
        unchangedRows: 0,
        unmatchedRows: 0,
        processedRows: 0,
        createdAt: job.createdAt,
        completedAt: null
      }, ...current.filter((item) => item.id !== job.id)].slice(0, 30));
      setNotice('Delivery/status update is running.');
      setStatusFile(null);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Status CSV update failed.');
    } finally {
      setBusyAction(null);
    }
  }

  function pstUploadKey(file: File) {
    return `pst:${file.name}:${file.size}:${file.lastModified}`;
  }

  function updatePstQueueItem(id: string, patch: Partial<PstQueueItem>) {
    setPstQueue((current) => current.map((item) => item.id === id ? { ...item, ...patch } : item));
  }

  function addPstFiles(files: FileList | File[]) {
    const incoming = Array.from(files).filter((file) => file.name.toLowerCase().endsWith('.pst'));
    if (incoming.length === 0) {
      setNotice('Choose one or more .pst files.');
      return;
    }
    setPstQueue((current) => {
      const existing = new Set(current.map((item) => `${item.name}:${item.size}:${item.file.lastModified}`));
      const next = incoming
        .filter((file) => !existing.has(`${file.name}:${file.size}:${file.lastModified}`))
        .map((file): PstQueueItem => ({
          id: window.crypto?.randomUUID?.() ?? `${Date.now()}-${file.name}`,
          file,
          name: file.name,
          size: file.size,
          uploadId: window.localStorage.getItem(`email-data-platform-${pstUploadKey(file)}`) ?? undefined,
          uploadedChunks: 0,
          chunkCount: Math.ceil(file.size / pstChunkSize),
          progress: 0,
          status: 'waiting'
        }));
      return [...current, ...next];
    });
    setNotice(`${formatNumber(incoming.length)} PST file${incoming.length === 1 ? '' : 's'} added to Inbox Sync.`);
  }

  async function uploadPstQueueItem(item: PstQueueItem) {
    if (!navigator.onLine) {
      updatePstQueueItem(item.id, { status: 'paused', error: 'Waiting for internet connection.' });
      return;
    }
    updatePstQueueItem(item.id, { status: 'uploading', error: undefined });
    const savedUploadId = item.uploadId ?? window.localStorage.getItem(`email-data-platform-${pstUploadKey(item.file)}`) ?? undefined;
    const init = await initOutlookPstChunkUpload({
      fileName: item.name,
      fileSize: item.size,
      chunkSize: pstChunkSize,
      uploadId: savedUploadId,
      uploadKey: pstUploadKey(item.file),
      uploadDate: pstUploadDate || undefined,
      defaultSentDate: pstFallbackDate || undefined
    });
    window.localStorage.setItem(`email-data-platform-${pstUploadKey(item.file)}`, init.uploadId);
    const uploaded = new Set(init.uploadedChunks);
    updatePstQueueItem(item.id, {
      uploadId: init.uploadId,
      chunkCount: init.chunkCount,
      uploadedChunks: uploaded.size,
      progress: Math.round((uploaded.size / Math.max(init.chunkCount, 1)) * 80)
    });

    for (let index = 0; index < init.chunkCount; index += 1) {
      if (uploaded.has(index)) continue;
      if (!navigator.onLine) {
        updatePstQueueItem(item.id, { status: 'paused', error: 'Upload paused while offline.' });
        return;
      }
      const start = index * pstChunkSize;
      const chunk = item.file.slice(start, Math.min(start + pstChunkSize, item.size));
      await uploadOutlookPstChunk(init.uploadId, index, chunk);
      uploaded.add(index);
      updatePstQueueItem(item.id, {
        uploadedChunks: uploaded.size,
        progress: Math.round((uploaded.size / Math.max(init.chunkCount, 1)) * 80)
      });
    }

    updatePstQueueItem(item.id, { status: 'processing', progress: 90 });
    const job = await completeOutlookPstChunkUpload({
      uploadId: init.uploadId,
      fileName: item.name,
      uploadDate: pstUploadDate || undefined,
      defaultSentDate: pstFallbackDate || undefined
    });
    window.localStorage.removeItem(`email-data-platform-${pstUploadKey(item.file)}`);
    setJobs((current) => [job, ...current.filter((existing) => existing.id !== job.id)].slice(0, 10));
    setStatusBatches((current) => [{
      id: job.id,
      fileName: job.fileName,
      status: 'queued' as const,
      totalRows: 0,
      validRows: 0,
      invalidRows: 0,
      duplicateRows: 0,
      insertedRows: 0,
      updatedRows: 0,
      protectedRows: 0,
      unchangedRows: 0,
      unmatchedRows: 0,
      processedRows: 0,
      createdAt: job.createdAt,
      completedAt: null
    }, ...current.filter((existing) => existing.id !== job.id)].slice(0, 30));
    updatePstQueueItem(item.id, { status: 'completed', progress: 100, jobId: job.id, error: undefined });
  }

  async function startPstQueueSync(targetId?: string) {
    if (!canWrite) {
      setNotice('You do not have permission to sync PST archives.');
      return;
    }
    const targets = pstQueue.filter((item) => targetId ? item.id === targetId : ['waiting', 'paused', 'failed'].includes(item.status));
    if (targets.length === 0) {
      setNotice('Add PST files to the queue first.');
      return;
    }
    setBusyAction('outlookPstQueue');
    try {
      for (const item of targets) {
        try {
          await uploadPstQueueItem(item);
        } catch (error) {
          updatePstQueueItem(item.id, {
            status: navigator.onLine ? 'failed' : 'paused',
            error: error instanceof Error ? error.message : 'PST sync failed.'
          });
        }
      }
      setNotice('PST upload queue submitted. Processing jobs are visible below.');
      setRefreshKey((value) => value + 1);
    } finally {
      setBusyAction(null);
    }
  }

  async function reviewStatusRecovery() {
    setBusyAction('statusRecoveryReview');
    try {
      const candidates = await listRecoverableStatusChanges();
      setRecoverableStatusChanges(candidates);
      setNotice(candidates.length ? `${formatNumber(candidates.length)} recoverable status changes found.` : 'No recoverable Positive/Failed to Negative changes found from the last 7 days.');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Could not review status recovery candidates.');
    } finally {
      setBusyAction(null);
    }
  }

  async function restoreStatusRecovery() {
    if (!recoverableStatusChanges?.length) return;
    if (!window.confirm(`Restore ${recoverableStatusChanges.length} records to their previous Positive or Failed status?`)) return;
    setBusyAction('statusRecoveryRestore');
    try {
      const result = await restoreRecoverableStatusChanges(recoverableStatusChanges.map((candidate) => candidate.eventId));
      setRecoverableStatusChanges([]);
      setNotice(`Restored ${formatNumber(result.restored)} records to their previous status.`);
      setRefreshKey((value) => value + 1);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Could not restore the selected status changes.');
    } finally {
      setBusyAction(null);
    }
  }

  async function handleCreateMailLogSource() {
    if (!mailLogName.trim() || !mailLogUrl.trim()) {
      setNotice('Enter a server name and its PowerMTA logs URL.');
      return;
    }
    setBusyAction('mailLogCreate');
    try {
      const payload = {
        name: mailLogName,
        portalUrl: mailLogUrl,
        intervalMinutes: mailLogInterval,
        lookbackDays: mailLogLookback,
        allowInsecureTls: mailLogInsecureTls
      };
      const source = editingMailLogSource ? await updateMailLogSource(editingMailLogSource.id, payload) : await createMailLogSource(payload);
      setMailLogSources((current) => editingMailLogSource ? current.map((item) => item.id === source.id ? source : item) : [source, ...current]);
      setMailLogName('');
      setMailLogUrl('');
      setEditingMailLogSource(null);
      setNotice(editingMailLogSource ? 'Mail server settings updated.' : 'Mail server connected. Use Sync now to process its latest accounting files.');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Could not connect the mail server.');
    } finally {
      setBusyAction(null);
    }
  }

  function startEditingMailLogSource(source: MailLogSource) {
    setEditingMailLogSource(source); setMailLogName(source.name); setMailLogUrl(source.portalUrl); setMailLogInterval(source.intervalMinutes); setMailLogLookback(source.lookbackDays); setMailLogInsecureTls(source.allowInsecureTls);
  }

  async function handleDeleteMailLogSource(source: MailLogSource) {
    if (!window.confirm(`Delete ${source.name}? Its sync history will be removed, but lead data will remain unchanged.`)) return;
    setBusyAction(`mailLogDelete:${source.id}`);
    try { await deleteMailLogSource(source.id); setMailLogSources((current) => current.filter((item) => item.id !== source.id)); if (editingMailLogSource?.id === source.id) setEditingMailLogSource(null); setNotice('Mail server removed. Existing lead status updates were not changed.'); }
    catch (error) { setNotice(error instanceof Error ? error.message : 'Could not delete the mail server.'); }
    finally { setBusyAction(null); }
  }

  async function handleMailLogSync(source: MailLogSource) {
    setBusyAction(`mailLog:${source.id}`);
    try {
      const result = await syncMailLogSource(source.id, { includeLogs: true });
      const payload = await listMailLogSources();
      setMailLogSources(payload.rows);
      setRefreshKey((value) => value + 1);
      setNotice(`${source.name}: ${result.processedFiles} mail file(s) processed, ${result.updatedRows} lead status update(s).`);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Mail-log sync failed.');
    } finally {
      setBusyAction(null);
    }
  }

  function toggleMailLogSourceSelection(id: string) {
    setSelectedMailLogSourceIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllMailLogSources() {
    setSelectedMailLogSourceIds((current) => current.size === mailLogSources.length
      ? new Set()
      : new Set(mailLogSources.map((source) => source.id)));
  }

  async function handleSelectedMailLogSync(force = false, allHistory = false, sourceOverride?: MailLogSource[]) {
    const selectedSources = sourceOverride ?? mailLogSources.filter((source) => selectedMailLogSourceIds.has(source.id));
    if (selectedSources.length === 0) {
      setNotice('Select at least one mail server to sync.');
      return;
    }
    const confirmation = allHistory
      ? `Deep sync all ${selectedSources.length} connected server(s)? This re-reads every accounting CSV and logging file currently available on every server, including old files. It may take time and will apply the latest delivery outcomes.`
      : force
        ? `Full re-sync ${selectedSources.length} selected server(s)? Each server will re-read accounting CSV and logging files within its configured history window and apply the latest delivery statuses.`
        : null;
    if (confirmation && !window.confirm(confirmation)) return;

    setBusyAction('mailLogBatch');
    setMailLogBatchProgress({ total: selectedSources.length, completed: 0, processedFiles: 0, updatedRows: 0, failures: [] });
    let processedFiles = 0;
    let updatedRows = 0;
    let completed = 0;
    const failures: string[] = [];
    await Promise.all(selectedSources.map(async (source) => {
      try {
        const result = await syncMailLogSource(source.id, { force, allHistory, includeLogs: true });
        processedFiles += result.processedFiles;
        updatedRows += result.updatedRows;
      } catch (error) {
        failures.push(`${source.name}: ${error instanceof Error ? error.message : 'Sync failed.'}`);
      }
      setMailLogBatchProgress({ total: selectedSources.length, completed: ++completed, processedFiles, updatedRows, failures: [...failures] });
    }));
    try {
      const payload = await listMailLogSources();
      setMailLogSources(payload.rows);
      setRefreshKey((value) => value + 1);
      const syncLabel = allHistory ? 'Deep sync' : force ? 'Full re-sync' : 'Sync';
      setNotice(`${syncLabel} complete: ${formatNumber(processedFiles)} mail file(s) processed and ${formatNumber(updatedRows)} lead status update(s).${failures.length ? ` ${failures.length} server(s) failed; see progress details.` : ''}`);
    } finally {
      setBusyAction(null);
    }
  }

  async function handleCreateCampaign() {
    if (!newCampaignName.trim()) {
      setNotice('Campaign name is required.');
      return;
    }

    setBusyAction('campaign');
    try {
      await createCampaign({
        name: newCampaignName,
        description: newCampaignDescription
      });
      setNewCampaignName('');
      setNewCampaignDescription('');
      setNotice('Campaign saved.');
      setRefreshKey((value) => value + 1);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Campaign save failed.');
    } finally {
      setBusyAction(null);
    }
  }

  async function handleCreateUser() {
    if (!canAdmin) {
      return;
    }

    setBusyAction('user');
    try {
      await createUser({
        email: newUserEmail,
        displayName: newUserName,
        workspaceName: newUserWorkspace.trim() || undefined,
        role: newUserRole,
        password: newUserPassword
      });
      setNewUserEmail('');
      setNewUserName('');
      setNewUserWorkspace('');
      setNewUserPassword('');
      setNotice('User created.');
      setRefreshKey((value) => value + 1);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'User creation failed.');
    } finally {
      setBusyAction(null);
    }
  }

  async function handleChangePassword(event: React.FormEvent) {
    event.preventDefault();
    setBusyAction('password');
    try {
      await changePassword(currentPassword, nextPassword);
      setUser(null);
      setNotice('');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Password change failed.');
    } finally {
      setBusyAction(null);
    }
  }

  async function handleLogout() {
    await logout().catch(() => setAuthToken(null));
    setUser(null);
  }

  function goNext() {
    if (!nextCursor) {
      return;
    }

    setCursorStack((current) => [...current, currentCursor ?? '']);
    setCurrentCursor(nextCursor);
  }

  function goPrevious() {
    setCursorStack((current) => {
      const next = [...current];
      const previousCursor = next.pop();
      setCurrentCursor(previousCursor || undefined);
      return next;
    });
  }

  async function loadMoreLeads() {
    if (!nextCursor || loadingMore || leads.length >= 5000) return;
    const requestSeq = leadRequestSeq.current;
    const cursor = nextCursor;
    const loadFilters = filters;
    setLoadingMore(true);
    try {
      const page = await listLeads({ ...loadFilters, limit: Math.min(loadFilters.limit ?? 100, 5000 - leads.length) }, cursor);
      if (requestSeq !== leadRequestSeq.current || cursor !== nextCursor) {
        return;
      }
      setLeads((current) => [...current, ...page.rows]);
      setNextCursor(page.nextCursor);
    } catch (error) {
      if (requestSeq === leadRequestSeq.current) {
        setNotice(error instanceof Error ? error.message : 'Could not load more leads.');
      }
    } finally {
      if (requestSeq === leadRequestSeq.current) {
        setLoadingMore(false);
      }
    }
  }
  loadMoreRef.current = () => { void loadMoreLeads(); };

  function applyLeadCommand(partial: LeadFilters) {
    setFilters((current) => ({ ...current, ...partial, preset: partial.preset ?? current.preset ?? '' }));
    setCurrentCursor(undefined);
    setCursorStack([]);
    setActiveView('leads');
    setCommandOpen(false);
  }

  function openEnrichmentLeads(enrichment: LeadFilters['enrichment'] = '') {
    setAllMatchedSelected(false);
    setFilters({ ...defaultFilters, preset: '', enrichment });
    setSelectedIds(new Set());
    setCurrentCursor(undefined);
    setCursorStack([]);
    setActiveView('leads');
  }

  const commandTerm = commandQuery.trim();
  const commandActions = [
    { label: 'Open Leads', detail: 'Search, filter, update and export leads', run: () => setActiveView('leads') },
    { label: 'Open Imports', detail: 'Upload lead and status files', run: () => setActiveView('imports') },
    { label: 'Open Media Extractor', detail: 'Extract leads from PDFs, images and documents', run: () => setActiveView('mediaExtractor') },
    { label: 'Open Data Sources', detail: 'View source files and file analytics', run: () => setActiveView('sources') },
    { label: 'Open Server Sync', detail: 'Sync accounting files from mail servers', run: () => setActiveView('serverSync') },
    { label: 'Open Enrichment Hub', detail: 'Track enrichment jobs and source scans', run: () => setActiveView('enrichmentHub') },
    { label: commandTerm ? `Email contains "${commandTerm}"` : 'Email contains search', detail: 'Filter email IDs by keyword or domain', run: () => commandTerm.length >= 3 ? applyLeadCommand({ emailContains: commandTerm, preset: '' }) : setNotice('Type at least 3 characters for email contains search.') },
    { label: commandTerm ? `Search all fields for "${commandTerm}"` : 'Search all lead fields', detail: 'Search name, company, email, source and location', run: () => commandTerm.length >= 3 ? applyLeadCommand({ q: commandTerm, preset: '' }) : setNotice('Type at least 3 characters for global search.') },
    { label: 'Google / Gmail leads', detail: 'Filter consumer Google email domains', run: () => applyLeadCommand({ provider: 'google', preset: '' }) },
    { label: 'Microsoft leads', detail: 'Filter Outlook, Hotmail, Live, MSN domains', run: () => applyLeadCommand({ provider: 'microsoft', preset: '' }) },
    { label: 'Private domain mail', detail: 'Filter non-consumer business domains', run: () => applyLeadCommand({ provider: 'private', preset: '' }) },
    { label: 'Export current CSV', detail: 'Download the current filtered lead scope', run: () => void safeDownload(exportPath(filters), 'leads.csv') },
    { label: 'Export current XLSX', detail: 'Download the current filtered lead scope as Excel', run: () => void safeDownload(exportPath(filters, 'xlsx'), 'leads.xlsx') },
    { label: 'Queue export job', detail: 'Build the current filtered export in the background', run: () => void handleQueueExport() }
  ].filter((action) => {
    const query = commandTerm.toLowerCase();
    return !query || `${action.label} ${action.detail}`.toLowerCase().includes(query) || action.label.includes(commandTerm);
  });
  const enrichmentJobs = jobs.filter((job) => job.jobType === 'source_enrichment');
  const enrichmentActiveCount = enrichmentJobs.filter((job) => job.status === 'queued' || job.status === 'running').length;

  useEffect(() => {
    if (!user || activeView !== 'enrichmentHub') return;
    let cancelled = false;
    let pending = false;
    const refreshSummary = () => {
      if (pending || document.hidden) return;
      pending = true;
      if (!enrichmentSummary) setEnrichmentSummaryLoading(true);
      void getEnrichmentWorkspaceSummary()
        .then((summary) => { if (!cancelled) setEnrichmentSummary(summary); })
        .catch((error) => {
          if (!cancelled) setNotice(error instanceof Error ? error.message : 'Enrichment totals could not be loaded.');
        })
        .finally(() => {
          pending = false;
          if (!cancelled) setEnrichmentSummaryLoading(false);
        });
    };
    refreshSummary();
    const hasRunningWork = enrichmentActiveCount > 0 || (enrichmentSummary?.activeJobs ?? 0) > 0 || (enrichmentSummary?.queuedJobs ?? 0) > 0;
    if (!hasRunningWork) return () => { cancelled = true; };
    const timer = window.setInterval(refreshSummary, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [activeView, enrichmentActiveCount, enrichmentSummary?.activeJobs, enrichmentSummary?.queuedJobs, refreshKey, user]);

  if (authChecking) {
    return (
      <main className="login-shell">
        <div className="login-card">Loading...</div>
      </main>
    );
  }

  if (!user) {
    return <LoginScreen onLogin={setUser} />;
  }

  return (
    <main className="app-shell">
      <aside className="sidebar">
        <div className="brand-lockup">
          <div className="brand-mark">D</div>
          <div>
            <strong>Data Ops</strong>
            <span>{user.workspaceName}</span>
          </div>
        </div>

        <nav aria-label="Primary">
          {navGroups.map((group) => {
            const items = navItems.filter(({ view, group: itemGroup }) => itemGroup === group && (
              view !== 'audit' || canAudit
            ) && (
              view !== 'suppression' || canAdmin
            ) && (
              (view !== 'performance' && view !== 'backups') || canAdmin
            ));
            if (items.length === 0) return null;
            return <div className="nav-group" key={group}><span>{group}</span>{items.map(({ view, icon: Icon, label }) => (
              <button className={`nav-item ${activeView === view ? 'active' : ''}`} key={view} type="button" onClick={() => setActiveView(view)}>
                <Icon size={18} />{label}
              </button>
            ))}</div>;
          })}
        </nav>
        <div className="sidebar-spacer" />
        <button className="sidebar-user" type="button" onClick={() => setActiveView('settings')}>
          <span className="user-avatar">{userInitials || 'DO'}</span>
          <span>
            <strong>{user.displayName}</strong>
            <small>{user.role} workspace</small>
          </span>
        </button>
      </aside>

      <section className="workspace">
        <header className="topbar">
          <div>
            <p className="eyebrow">{activeMeta.eyebrow}</p>
            <h1>{activeMeta.title}</h1>
            <p className="page-summary">{activeMeta.summary}</p>
          </div>
          <div className="top-actions">
            {followUpAlertCount > 0 && (
              <button className="follow-up-alert" type="button" onClick={() => setActiveView('followups')}>
                <BellRing size={16} />
                {followUpAlertCount} follow-up{followUpAlertCount === 1 ? '' : 's'} due
              </button>
            )}
            <span className="workspace-chip"><i />{user.workspaceName}</span>
            <button className="ghost-button command-button" type="button" onClick={() => setCommandOpen(true)}>
              <Search size={17} />
              Ctrl K
            </button>
            <button className="ghost-button" type="button" onClick={() => setRefreshKey((value) => value + 1)}>
              <RefreshCcw size={17} />
              Refresh
            </button>
            <div className="export-menu-wrap">
              <button className="primary-button" type="button" aria-expanded={exportMenuOpen} onClick={() => setExportMenuOpen((value) => !value)}>
                <Download size={17} />
                Export
              </button>
              {exportMenuOpen && (
                <div className="export-menu">
                  <button type="button" onClick={() => { setExportMenuOpen(false); void safeDownload(exportPath(filters), 'leads.csv'); }}><Download size={15} />CSV for current scope</button>
                  <button type="button" onClick={() => { setExportMenuOpen(false); void safeDownload(exportPath(filters, 'xlsx'), 'leads.xlsx'); }}><Download size={15} />XLSX for current scope</button>
                  <button type="button" disabled={busyAction === 'exportQueue'} onClick={() => { setExportMenuOpen(false); void handleQueueExport(); }}><Database size={15} />Queue large export</button>
                </div>
              )}
            </div>
            <button className="ghost-button" type="button" onClick={() => void handleLogout()}>
              <LogOut size={17} />
              Logout
            </button>
          </div>
        </header>

        {notice && (
          <div className="notice" role="status">
            <AlertTriangle size={17} />
            {notice}
          </div>
        )}
        {activeView !== 'leads' && activeView !== 'directoryExtractor' && <WorkflowGuide activeView={activeView} onNavigate={setActiveView} />}
        <datalist id="country-options">
          {countryOptions.map((country) => (
            <option key={country} value={country} />
          ))}
        </datalist>

        {activeView === 'leads' && (
          <>
            <details className={`lead-total-panel ${statsRefreshing ? 'refreshing' : ''}`} open={totalsOpen} onToggle={(event) => { const open = event.currentTarget.open; setTotalsOpen(open); localStorage.setItem('lead-totals-open', String(open)); }}>
              <summary><span>Workspace totals</span><strong>{stats ? `${formatNumber(stats.total)} leads` : 'Loading totals'}</strong><small>{statsUpdatedAt ? `Updated ${formatDateTime(statsUpdatedAt)}` : 'Loading latest counts'}</small></summary>
              <section className="stat-grid lead-stat-grid" aria-label="Lead totals">
                {topStats.map((stat) => {
                  const Icon = stat.icon;
                  return (
                    <article className="stat-card" key={stat.label}>
                      <div className="stat-icon"><Icon size={19} /></div>
                      <span>{stat.label}</span>
                      <strong>{stats ? formatNumber(stat.value) : <i className="stat-skeleton" />}</strong>
                      <small>Workspace total</small>
                    </article>
                  );
                })}
              </section>
            </details>

            <section className="lead-command-strip" aria-label="Lead actions">
              <span>Workspace actions</span>
              <button type="button" onClick={() => setActiveView('imports')} disabled={!canWrite}><FileUp size={16} />Upload list</button>
              <button type="button" onClick={() => setActiveView('paste')} disabled={!canWrite}><FileText size={16} />Paste leads</button>
              <button type="button" onClick={() => setActiveView('mediaExtractor')} disabled={!canWrite}><Sparkles size={16} />Extract media</button>
              <button type="button" onClick={() => setSingleLeadOpen(true)} disabled={!canWrite}><UserPlus size={16} />Single lead</button>
              <button type="button" onClick={() => setActiveView('dedupe')} disabled={!canWrite}><ShieldCheck size={16} />Dedupe</button>
              <button type="button" onClick={() => setActiveView('imports')} disabled={!canWrite}><ListChecks size={16} />Update statuses</button>
            </section>

            {selectedSource && (
              <section className="source-context" aria-label="Selected data source">
                <div>
                  <span className="section-kicker"><FolderArchive size={14} /> {filters.sourceSheet ? 'Viewing worksheet' : 'Viewing full source'}</span>
                  <strong>{selectedSource.sourceFile}</strong>
                  {filters.sourceSheet ? <small>Sheet: {filters.sourceSheet}</small> : <small>This file scope: {formatNumber(selectedSource.leadCount)} usable leads from {formatNumber(selectedSource.originalRows)} received rows · uploaded {formatDate(selectedSource.uploadDate)}</small>}
                </div>
                {!filters.sourceSheet && !selectedSource.countsPending && <div className="source-context-metrics">
                  <span>{formatNumber(selectedSource.positiveCount)} positive</span>
                  <span>{formatNumber(selectedSource.negativeCount)} negative</span>
                  <span>{formatNumber(selectedSource.failedCount)} failed</span>
                  <span>{formatNumber(selectedSource.suppressedCount)} suppressed</span>
                </div>}
                <div>
                  <button className="ghost-button" type="button" onClick={() => setActiveView('sources')}>Source history</button>
                  <button className="ghost-button" type="button" onClick={clearSourceFilter}>Clear source</button>
                </div>
              </section>
            )}

            <section className="lead-explorer" aria-label="Lead explorer controls">
              <div className="lead-explorer-main">
                <label className="search-field">
                  <Search size={17} />
                  <input value={filters.q ?? ''} onChange={(event) => setFilterValue('q', event.target.value)} placeholder="Search email, name, company or website" />
                </label>
                <label className="search-field email-only-search">
                  <MailCheck size={17} />
                  <input value={filters.emailContains ?? ''} onChange={(event) => setFilterValue('emailContains', event.target.value)} placeholder="Email contains, e.g. gmail or @company.com" />
                </label>
                <label>View<select value={filters.preset ?? ''} onChange={(event) => setFilterValue('preset', event.target.value as LeadFilters['preset'])}><option value="">All leads</option><option value="fresh">Fresh leads</option><option value="unsent">Unsent leads</option></select></label>
                <label>Status<select value={filters.status ?? ''} onChange={(event) => setFilterValue('status', event.target.value as LeadFilters['status'])}><option value="">Any status</option>{leadStatuses.map((status) => <option key={status} value={status}>{statusLabels[status]}</option>)}</select></label>
                <label>Data type<select value={filters.dataType ?? ''} onChange={(event) => setFilterValue('dataType', event.target.value as LeadFilters['dataType'])}><option value="">Any type</option>{leadDataTypes.map((dataType) => <option key={dataType} value={dataType}>{dataTypeLabels[dataType]}</option>)}</select></label>
                <label>Country<input value={filters.country ?? ''} onChange={(event) => setFilterValue('country', event.target.value)} placeholder="Any country" /></label>
                <button className={`filter-toggle ${advancedFiltersOpen ? 'active' : ''}`} type="button" onClick={() => setAdvancedFiltersOpen((value) => !value)}><SlidersHorizontal size={16} />Filters{activeFilterCount > 6 ? ` (${activeFilterCount - 6})` : ''}</button>
                <div className="column-menu-wrap">
                  <button className={`filter-toggle ${columnMenuOpen ? 'active' : ''}`} type="button" onClick={() => setColumnMenuOpen((value) => !value)}><Columns3 size={16} />Columns</button>
                  {columnMenuOpen && <div className="column-menu">{(Object.keys(leadColumnLabels) as LeadTableColumn[]).map((column) => <label key={column}><input type="checkbox" checked={visibleColumns.has(column)} onChange={() => toggleVisibleColumn(column)} />{leadColumnLabels[column]}</label>)}</div>}
                </div>
                <label className="density-control">Density<select value={tableDensity} onChange={(event) => setTableDensity(event.target.value as 'comfortable' | 'compact')}><option value="compact">Compact</option><option value="comfortable">Comfortable</option></select></label>
              </div>
              {activeFilterCount > 0 && <div className="active-filter-row"><span>{activeFilterCount} active filter{activeFilterCount === 1 ? '' : 's'}</span>{filters.enrichment && <label>Enrichment <select value={filters.enrichment} onChange={event => setFilterValue('enrichment', event.target.value as LeadFilters['enrichment'])}><option value="">All</option><option value="saved">Scans saved</option><option value="failed">Retrieval failed</option><option value="pending">Not yet enriched</option><option value="full">Full profiles</option><option value="partial">Partial profiles</option><option value="no_data">No website data</option><option value="legacy">Earlier scans</option></select></label>}<button type="button" onClick={clearAllLeadFilters}>Clear filters</button></div>}
              {advancedFiltersOpen && <div className="advanced-filter-grid">
                <label>Campaign<select value={filters.campaignId ?? ''} onChange={(event) => setFilterValue('campaignId', event.target.value)}><option value="">Any campaign</option>{campaigns.map((campaign) => <option key={campaign.id} value={campaign.id}>{campaign.name}</option>)}</select></label>
                <label>Industry<input value={filters.industry ?? ''} onChange={(event) => setFilterValue('industry', event.target.value)} placeholder="CPA, retail, VA" /></label>
                <label className="checkbox-field"><input type="checkbox" checked={Boolean(filters.reviewOnly)} onChange={event => setFilterValue('reviewOnly', event.target.checked || undefined)} /> Enrichment suggestions to review</label>
                <label>Website update evidence<select value={filters.websiteUpdated ?? ''} onChange={event => setFilterValue('websiteUpdated', event.target.value)}><option value="">All</option><option value="known">Date available</option><option value="unknown">Unknown / not checked</option></select></label>
                <label>Website updated from<input type="date" value={filters.websiteUpdatedFrom ?? ''} onChange={event => setFilterValue('websiteUpdatedFrom', event.target.value)} /></label>
                <label>Website updated to<input type="date" value={filters.websiteUpdatedTo ?? ''} onChange={event => setFilterValue('websiteUpdatedTo', event.target.value)} /></label>
                <label className="checkbox-field"><input type="checkbox" checked={Boolean(filters.industryMissing)} onChange={(event) => setFilterValue('industryMissing', event.target.checked || undefined)} /> Missing industry only</label>
                <label>Provider<select value={filters.provider ?? ''} onChange={(event) => setFilterValue('provider', event.target.value as LeadFilters['provider'])}><option value="">Any provider</option><option value="google">Google / Gmail</option><option value="microsoft">Microsoft</option><option value="private">Private / domain mail</option></select></label>
                <label>Email type<select value={filters.emailType ?? ''} onChange={(event) => setFilterValue('emailType', event.target.value as LeadFilters['emailType'])}><option value="">Any email type</option><option value="generic">Generic email IDs</option><option value="other">Other emails</option></select></label>
                <label>Time zone<input value={filters.timeZone ?? ''} onChange={(event) => setFilterValue('timeZone', event.target.value)} placeholder="EST, CST, PST" /></label>
                <label>Server / source<input value={filters.sourceContains ?? ''} onChange={(event) => setFilterValue('sourceContains', event.target.value)} placeholder="File or server name" /></label>
                <label>Uploaded from<input type="date" value={filters.uploadDateFrom ?? ''} onChange={(event) => setFilterValue('uploadDateFrom', event.target.value)} /></label>
                <label>Uploaded to<input type="date" value={filters.uploadDateTo ?? ''} onChange={(event) => setFilterValue('uploadDateTo', event.target.value)} /></label>
                <label>Sent from<input type="date" value={filters.sentDateFrom ?? ''} onChange={(event) => setFilterValue('sentDateFrom', event.target.value)} /></label>
                <label>Sent to<input type="date" value={filters.sentDateTo ?? ''} onChange={(event) => setFilterValue('sentDateTo', event.target.value)} /></label>
                <label>Rows per load<select value={filters.limit ?? 100} onChange={(event) => setFilterValue('limit', Number(event.target.value))}><option value={100}>100 rows</option><option value={250}>250 rows</option><option value={500}>500 rows</option></select></label>
              </div>}
              <div className="lead-result-strip" aria-label="Lead result summary">
                <div aria-live="polite"><span>Total matches</span><strong>{hasExactLeadTotal ? formatNumber(displayedLeadTotal) : leadCountError ? 'Count unavailable' : 'Counting matches…'}</strong>{leadCountError && <><small role="alert">{leadCountError}</small><button type="button" className="ghost-button" onClick={() => setLeadCountRetry(value => value + 1)}>Retry count</button></>}</div>
                <div><span>Loaded in table</span><strong>{formatNumber(leads.length)}</strong></div>
                <div><span>Selected</span><strong>{allMatchedSelected && !hasExactLeadTotal ? 'All matches (count pending)' : formatNumber(selectedCount)}</strong></div>
                <div><span>Bulk/export scope</span><strong>{allMatchedSelected ? 'All matched selected' : hasExactLeadTotal ? `${formatNumber(displayedLeadTotal)} records` : 'Current filters'}</strong></div>
              </div>
            </section>

            {showBulkToolbar && <section className="bulk-toolbar" aria-label="Bulk actions">
              <span>{bulkScopeLabel}</span>
              <button className="ghost-button select-matched-button" type="button" disabled={!canWrite || (hasExactLeadTotal && displayedLeadTotal === 0)} onClick={() => { setAllMatchedSelected(true); setSelectedIds(new Set()); }}>
                <CheckCircle2 size={17} />
                Select all matched
              </button>
              {allMatchedSelected && <button className="ghost-button select-matched-button" type="button" onClick={() => { setAllMatchedSelected(false); setSelectedIds(new Set()); }}><X size={17} />Clear all</button>}
              <select value={bulkUpdateTarget} onChange={(event) => setBulkUpdateTarget(event.target.value as BulkUpdateTarget)} disabled={!canWrite}>
                <option value="status">Status</option>
                <option value="industry">Industry</option>
                <option value="dataType">Data type</option>
                <option value="campaignId">Campaign</option>
                <option value="tag">Append tag</option>
              </select>
              {bulkUpdateTarget === 'status' && (
                <select value={bulkStatusValue} onChange={(event) => setBulkStatusValue(event.target.value as LeadStatus)} disabled={!canWrite}>
                  {leadStatuses.map((status) => (
                    <option key={status} value={status}>
                      {statusLabels[status]}
                    </option>
                  ))}
                </select>
              )}
              {bulkUpdateTarget === 'industry' && <input className="bulk-value-input" value={bulkIndustryValue} onChange={(event) => setBulkIndustryValue(event.target.value)} placeholder="Industry value" disabled={!canWrite} />}
              {bulkUpdateTarget === 'dataType' && (
                <select value={bulkDataTypeValue} onChange={(event) => setBulkDataTypeValue(event.target.value as LeadDataType)} disabled={!canWrite}>
                  {leadDataTypes.map((dataType) => <option key={dataType} value={dataType}>{dataTypeLabels[dataType]}</option>)}
                </select>
              )}
              {bulkUpdateTarget === 'campaignId' && (
                <select value={bulkCampaignValue} onChange={(event) => setBulkCampaignValue(event.target.value)} disabled={!canWrite}>
                  <option value="">Unassigned</option>
                  {campaigns.map((campaign) => <option key={campaign.id} value={campaign.id}>{campaign.name}</option>)}
                </select>
              )}
              {bulkUpdateTarget === 'tag' && <input className="bulk-value-input" value={bulkTagValue} onChange={(event) => setBulkTagValue(event.target.value)} placeholder="Tag, e.g. cpa-lead-2026" disabled={!canWrite} />}
              <button type="button" onClick={handleBulkStatus} disabled={!canWrite || busyAction !== null}>
                <ListChecks size={17} />
                Change Selected
              </button>
              <button type="button" onClick={handleFilteredStatus} disabled={!canWrite || busyAction !== null}>
                <SlidersHorizontal size={17} />
                Update all matched
              </button>
            </section>}

            <LeadTable
              leads={leads}
              loading={loading}
              selectedIds={selectedIds}
              allMatchedSelected={allMatchedSelected}
              visibleColumns={visibleColumns}
              sortBy={filters.sortBy}
              sortDir={filters.sortDir}
              density={tableDensity}
              maskSensitive={maskSensitive}
              onToggle={toggleSelected}
              onToggleAll={toggleAllSelected}
              onStatusChange={handleRowStatusChange}
              onOpenLead={(lead) => { if (!maskSensitive) setActiveLead(lead); }}
              onSort={setLeadSort}
            />

            <footer className="pagination-bar">
              <span>
                Showing {formatNumber(leads.length)} loaded records
                {hasExactLeadTotal ? ` of ${formatNumber(displayedLeadTotal)} matched` : ''}
                {nextCursor ? leads.length >= 5000 ? ' · window limit reached' : ' · more load automatically as you scroll' : ''}
              </span>
              {cursorStack.length > 0 && <button type="button" onClick={goPrevious}>Previous window</button>}
              {nextCursor && <button type="button" onClick={() => leads.length >= 5000 ? goNext() : void loadMoreLeads()} disabled={loadingMore}>{loadingMore ? 'Loading next batch...' : leads.length >= 5000 ? 'Next window' : `Load next ${formatNumber(filters.limit ?? 100)}`}</button>}
            </footer>
            <div ref={loadMoreSentinelRef} className="lead-load-sentinel" aria-hidden="true" />
          </>
        )}

        {activeView === 'imports' && (
          <>
            <section className="import-mode-grid" aria-label="Import workflow choices">
              <article className="import-mode-card active"><span>01</span><strong>Add leads</strong><small>Upload or paste new historical/fresh lead sheets with upload date, sent date, country, campaign and data type.</small></article>
              <article className="import-mode-card"><span>02</span><strong>Update statuses</strong><small>Apply sent, relayed, failed, positive, negative, junk or bounce results while protecting existing final outcomes.</small></article>
              <article className="import-mode-card"><span>03</span><strong>Review outcome</strong><small>Open status batches and job reports to see changed, protected, unmatched and newly-created records.</small></article>
            </section>
            <section className="operation-panel" ref={leadImportPanelRef}>
              <div className="panel-heading">
                <div><h2>Add leads</h2><p>Use this for new lists, old sent files, or pasted spreadsheet rows that should create source history.</p></div>
              </div>
              {leadImportError && <p className="mapping-issues" role="alert" tabIndex={-1} data-import-problem>{leadImportError}</p>}
              <div className="panel-grid">
                <label
                  className={`file-drop-zone ${leadDragActive ? 'drag-active' : ''}`}
                  onDragEnter={(event) => {
                    event.preventDefault();
                    setLeadDragActive(true);
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDragLeave={(event) => {
                    event.preventDefault();
                    if (event.currentTarget === event.target) {
                      setLeadDragActive(false);
                    }
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    setLeadDragActive(false);
                    chooseLeadFiles(Array.from(event.dataTransfer.files));
                  }}
                >
                  Lead file
                  <input type="file" multiple disabled={busyAction === 'leadImport'} accept={importFileTypes} onChange={(event) => { chooseLeadFiles(Array.from(event.target.files ?? [])); event.target.value = ''; }} />
                  <span>{leadFiles.length ? `${leadFiles.length} files selected` : 'Drop CSV/XLSX files here or choose files'}</span>
                </label>
                <label>
                  Campaign
                  <select value={leadCampaign} onChange={(event) => setLeadCampaign(event.target.value)}>
                    <option value="">Unassigned</option>
                    {campaigns.map((campaign) => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Default status
                  <select value={leadDefaultStatus} onChange={(event) => {
                    const status = event.target.value as LeadStatus;
                    setLeadDefaultStatus(status);
                    if (status === 'sent' && !leadDefaultSentDate) setLeadDefaultSentDate(todayForInput());
                  }}>
                    {leadStatuses.map((status) => (
                      <option key={status} value={status}>
                        {statusLabels[status]}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Data type
                  <select value={leadDataType} onChange={(event) => setLeadDataType(event.target.value as LeadDataType | '')}>
                    <option value="">Auto from file</option>
                    {leadDataTypes.map((dataType) => (
                      <option key={dataType} value={dataType}>
                        {dataTypeLabels[dataType]}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Default country
                  <input
                    list="country-options"
                    value={leadDefaultCountry}
                    onChange={(event) => setLeadDefaultCountry(event.target.value)}
                    placeholder="Auto from file"
                  />
                </label>
                <label>
                  Upload date
                  <input type="date" value={leadUploadDate} onChange={(event) => setLeadUploadDate(event.target.value)} />
                </label>
                <label>
                  Default sent date
                  <input type="date" value={leadDefaultSentDate} onChange={(event) => setLeadDefaultSentDate(event.target.value)} />
                </label>
                <label className="wide-field">
                  Paste spreadsheet rows
                  <textarea
                    disabled={busyAction === 'leadImport'}
                    value={leadPasteText}
                    onChange={(event) => {
                      setLeadPasteText(event.target.value);
                      if (event.target.value.trim()) {
                        setLeadFiles([]);
                      }
                    }}
                    placeholder="Email&#9;Name&#9;Status&#9;Sent Date&#9;Country&#10;alex@example.com&#9;Alex Morgan&#9;Relayed&#9;2026-09-13&#9;USA"
                  />
                </label>
                {leadFiles.map(item => <section className="wide-field" tabIndex={-1} ref={element => { if (element) leadFileSectionsRef.current.set(item.file, element); else leadFileSectionsRef.current.delete(item.file); }} key={`${item.file.name}-${item.file.size}-${item.file.lastModified}`}>
                  <div className="source-card-actions"><strong>{item.file.name}</strong><span>{(item.file.size / 1048576).toFixed(1)} MB</span><span role="status">{item.status === 'submitted' ? 'Submitted - processing in background' : item.status}</span><button type="button" disabled={busyAction === 'leadImport'} onClick={() => setLeadFiles(current => current.filter(entry => entry.file !== item.file))}>Remove</button></div>
                  {item.error && <p className="mapping-issues" role="alert" tabIndex={-1} data-import-problem>{item.error}</p>}
                  {item.status !== 'submitted' && /\.(xlsx|xlsm)$/i.test(item.file.name) && <fieldset disabled={busyAction === 'leadImport'} style={{ border: 0, padding: 0, minWidth: 0 }}><ImportFileReview file={item.file} onPlans={updateFilePlans} /></fieldset>}
                </section>)}
                <button className="primary-button" type="button" disabled={!canWrite || busyAction === 'leadImport'} onClick={handleLeadImport}>
                  <Upload size={17} />
                  Start Import
                </button>
              </div>
            </section>

            <section className="operation-panel">
              <div className="panel-heading">
                <div><h2>Update delivery statuses</h2><p>Use this when a mail/report file should update existing emails without changing the original upload date.</p></div>
              </div>
              <div className="panel-grid">
                <label
                  className={`file-drop-zone ${statusDragActive ? 'drag-active' : ''}`}
                  onDragEnter={(event) => {
                    event.preventDefault();
                    setStatusDragActive(true);
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDragLeave={(event) => {
                    event.preventDefault();
                    if (event.currentTarget === event.target) {
                      setStatusDragActive(false);
                    }
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    setStatusDragActive(false);
                    setStatusFile(event.dataTransfer.files?.[0] ?? null);
                  }}
                >
                  Delivery or status file
                  <input type="file" accept={importFileTypes} onChange={(event) => setStatusFile(event.target.files?.[0] ?? null)} />
                  <span>{statusFile ? statusFile.name : 'Drop CSV/XLSX here or choose file'}</span>
                </label>
                <label>
                  Update status
                  <select value={statusDefaultStatus} onChange={(event) => setStatusDefaultStatus(event.target.value as LeadStatus | '')}>
                    <option value="">Detect from file</option>
                    {leadStatuses.map((status) => (
                      <option key={status} value={status}>{statusLabels[status]}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Campaign
                  <select value={statusCampaign} onChange={(event) => setStatusCampaign(event.target.value)}>
                    <option value="">Keep existing</option>
                    {campaigns.map((campaign) => (
                      <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Data type for new leads
                  <select value={statusDataType} onChange={(event) => setStatusDataType(event.target.value as LeadDataType | '')}>
                    <option value="">Auto from file</option>
                    {leadDataTypes.map((dataType) => (
                      <option key={dataType} value={dataType}>{dataTypeLabels[dataType]}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Country for new leads
                  <input list="country-options" value={statusDefaultCountry} onChange={(event) => setStatusDefaultCountry(event.target.value)} placeholder="Auto from file" />
                </label>
                <label>
                  Import date for new leads
                  <input type="date" value={statusUploadDate} onChange={(event) => setStatusUploadDate(event.target.value)} />
                </label>
                <label>
                  Fallback sent date
                  <input type="date" value={statusDefaultSentDate} onChange={(event) => setStatusDefaultSentDate(event.target.value)} />
                </label>
                <label>
                  Date layout
                  <select value={statusDateOrder} onChange={(event) => setStatusDateOrder(event.target.value as 'auto' | 'dmy' | 'mdy')}>
                    <option value="auto">Automatic</option>
                    <option value="dmy">DD/MM/YYYY</option>
                    <option value="mdy">MM/DD/YYYY</option>
                  </select>
                </label>
                <label className="check-field">
                  <input type="checkbox" checked={statusCreateMissing} onChange={(event) => setStatusCreateMissing(event.target.checked)} />
                  Add missing emails as new leads
                </label>
                <label className="check-field">
                  <input type="checkbox" checked={statusAllowFinalStatusOverwrite} onChange={(event) => setStatusAllowFinalStatusOverwrite(event.target.checked)} />
                  Override existing Positive, Negative or Failed results
                </label>
                <div className="format-note">
                  Select Update status when every row in this file should receive one status. By default, existing Positive, Negative and Failed results are protected; enable the override only when importing a trusted correction file. Detect from file uses columns such as Email, Status, Reply Status and Sent Date.
                </div>
                <button className="ghost-button" type="button" disabled={!canWrite || busyAction === 'statusRecoveryReview'} onClick={() => void reviewStatusRecovery()}>
                  <RefreshCcw size={16} />
                  Review accidental changes
                </button>
                <button className="primary-button" type="button" disabled={!canWrite || busyAction === 'statusCsv'} onClick={handleStatusCsv}>
                  <ListChecks size={17} />
                  Apply Statuses
                </button>
              </div>
            </section>

            {recoverableStatusChanges !== null && (
              <section className="operation-panel">
                <div className="panel-heading">
                  <div><h2>Status recovery review</h2><p>Only the latest Positive/Failed to Negative changes from the last 7 days are listed.</p></div>
                  <span>{formatNumber(recoverableStatusChanges.length)} candidates</span>
                </div>
                {recoverableStatusChanges.length === 0 ? <div className="source-empty"><CheckCircle2 size={22} /><h2>No changes need recovery</h2></div> : (
                  <>
                    <div className="compact-table-shell"><table><thead><tr><th>Lead</th><th>Current</th><th>Restore to</th><th>Changed</th></tr></thead><tbody>{recoverableStatusChanges.map((candidate) => <tr key={candidate.eventId}><td><strong>{candidate.fullName || 'No name'}</strong><small className="table-subtle">{candidate.email}</small></td><td>{statusLabels[candidate.newStatus]}</td><td>{statusLabels[candidate.oldStatus]}</td><td>{formatDate(candidate.changedAt)}</td></tr>)}</tbody></table></div>
                    <div className="source-card-actions"><button className="primary-button" type="button" disabled={!canWrite || busyAction === 'statusRecoveryRestore'} onClick={() => void restoreStatusRecovery()}><RefreshCcw size={17} />Restore previous statuses</button><button className="ghost-button" type="button" onClick={() => setRecoverableStatusChanges(null)}>Close review</button></div>
                  </>
                )}
              </section>
            )}

            <StatusUpdateBatchesPanel batches={statusBatches} onOpen={openStatusBatchReport} />

            <JobsPanel jobs={jobs} onDownload={safeDownload} onOpen={openJobDetails} />
          </>
        )}

        {activeView === 'paste' && (
          <section className="operation-panel paste-panel">
            <div className="panel-heading">
              <div>
                <h2>Paste leads</h2>
                <p>Add fresh lead blocks or mark negative emails in bulk.</p>
              </div>
              <div className="paste-tabs" role="tablist" aria-label="Paste mode">
                <button type="button" className={pasteMode === 'leads' ? 'active' : ''} onClick={() => setPasteMode('leads')}>Lead blocks</button>
                <button type="button" className={pasteMode === 'negative' ? 'active' : ''} onClick={() => setPasteMode('negative')}>Negative emails</button>
              </div>
            </div>
            {pasteMode === 'leads' ? <div className="panel-grid paste-grid">
              <label>
                Campaign
                <select value={pasteCampaign} onChange={(event) => setPasteCampaign(event.target.value)}>
                  <option value="">Unassigned</option>
                  {campaigns.map((campaign) => (
                    <option key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Default status
                <select value={pasteDefaultStatus} onChange={(event) => {
                  const status = event.target.value as LeadStatus;
                  setPasteDefaultStatus(status);
                  if (status === 'sent' && !pasteDefaultSentDate) setPasteDefaultSentDate(todayForInput());
                }}>
                  {leadStatuses.map((status) => (
                    <option key={status} value={status}>
                      {statusLabels[status]}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Data type
                <select value={pasteDataType} onChange={(event) => setPasteDataType(event.target.value as LeadDataType | '')}>
                  <option value="">Auto from Industry</option>
                  {leadDataTypes.map((dataType) => (
                    <option key={dataType} value={dataType}>
                      {dataTypeLabels[dataType]}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Default country
                <input
                  list="country-options"
                  value={pasteDefaultCountry}
                  onChange={(event) => setPasteDefaultCountry(event.target.value)}
                  placeholder="Auto from text"
                />
              </label>
              <label>
                Upload date
                <input type="date" value={pasteUploadDate} onChange={(event) => setPasteUploadDate(event.target.value)} />
              </label>
              <label>
                Default sent date
                <input type="date" value={pasteDefaultSentDate} onChange={(event) => setPasteDefaultSentDate(event.target.value)} />
              </label>
              <button className="primary-button" type="button" disabled={!canWrite || busyAction === 'pasteImport'} onClick={handlePasteImport}>
                <Upload size={17} />
                Add To Table
              </button>
              <label className="wide-field">
                Lead blocks
                <textarea
                  className="paste-textarea"
                  value={pasteText}
                  onChange={(event) => setPasteText(event.target.value)}
                  placeholder={pastedLeadExample}
                />
              </label>
            </div> : <div className="panel-grid paste-grid">
              <label>Status<select value={negativePasteStatus} onChange={(event) => setNegativePasteStatus(event.target.value as 'negative_reply' | 'junk' | 'hard_bounce')}><option value="negative_reply">Negative Reply</option><option value="junk">Junk / Unsubscribed</option><option value="hard_bounce">Hard Bounce</option></select></label>
              <button className="primary-button" type="button" disabled={!canWrite || busyAction === 'negativePaste'} onClick={handleNegativePasteImport}><ShieldCheck size={17} />Mark Negative</button>
              <label className="wide-field">Negative email blocks<textarea className="paste-textarea" value={negativePasteText} onChange={(event) => setNegativePasteText(event.target.value)} placeholder="not interested: alex@example.com&#10;unsubscribe sam@example.com&#10;hard bounce: user@example.com" /></label>
            </div>}
            {pasteMode === 'leads' && <PasteLeadPreview
              text={pasteText}
              defaultStatus={pasteDefaultStatus}
              campaignId={pasteCampaign || undefined}
              dataType={pasteDataType || undefined}
              defaultCountry={pasteDefaultCountry.trim() || undefined}
              defaultSentDate={pasteDefaultSentDate || undefined}
              uploadDate={pasteUploadDate || undefined}
            />}
          </section>
        )}

        {activeView === 'directoryExtractor' && canWrite && <DirectoryExtractor />}
        {activeView === 'mediaExtractor' && (
          <section className="media-extractor-grid" aria-label="Media extractor">
            <section className="operation-panel">
              <div className="panel-heading">
                <div>
                  <h2>Extract leads from media</h2>
                  <p>Upload readable PDFs, documents, screenshots, images or text files, then review detected rows before saving.</p>
                </div>
                {mediaEngine && <span>{mediaEngine}</span>}
              </div>
              <div
                className={`drop-zone ${mediaDragActive ? 'active' : ''}`}
                onDragOver={(event) => { event.preventDefault(); setMediaDragActive(true); }}
                onDragLeave={() => setMediaDragActive(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setMediaDragActive(false);
                  setMediaFile(event.dataTransfer.files?.[0] ?? null);
                  setMediaRows([]);
                }}
              >
                <FileUp size={28} />
                <strong>{mediaFile ? mediaFile.name : 'Drop PDF, document or image here'}</strong>
                <span>PNG, JPG, WEBP, PDF, DOCX, TXT and CSV are accepted.</span>
                <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.doc,.docx" onChange={(event) => { setMediaFile(event.target.files?.[0] ?? null); setMediaRows([]); }} />
              </div>
              <div className="panel-grid campaign-form">
                <button className="ghost-button" type="button" onClick={() => setActiveView('directoryExtractor')}><Search size={17} />Directory Extractor</button>
              </div>
              <div className="panel-grid campaign-form">
                <label>Default status<select value={mediaDefaultStatus} onChange={(event) => setMediaDefaultStatus(event.target.value as LeadStatus)}>{leadStatuses.map((status) => <option key={status} value={status}>{statusLabels[status]}</option>)}</select></label>
                <label>Data type<select value={mediaDataType} onChange={(event) => setMediaDataType(event.target.value as LeadDataType | '')}><option value="">Auto from Industry</option>{leadDataTypes.map((dataType) => <option key={dataType} value={dataType}>{dataTypeLabels[dataType]}</option>)}</select></label>
                <label>Country<input list="country-options" value={mediaDefaultCountry} onChange={(event) => setMediaDefaultCountry(event.target.value)} placeholder="Auto from file" /></label>
                <label>Upload date<input type="date" value={mediaUploadDate} onChange={(event) => setMediaUploadDate(event.target.value)} /></label>
                <label>Source sheet name<input value={mediaSourceSheetName} onChange={(event) => setMediaSourceSheetName(event.target.value)} /></label>
                <button className="primary-button" type="button" disabled={!canWrite || !mediaFile || busyAction === 'mediaPreview'} onClick={handleMediaPreview}><Sparkles size={17} />Extract & Review</button>
              </div>
              {mediaWarnings.length > 0 && <div className="format-note">{mediaWarnings.join(' ')}</div>}
            </section>

            <section className="operation-panel">
              <div className="panel-heading">
                <div><h2>Review extracted rows</h2><p>Edit fields here before writing anything to the database.</p></div>
                <span>{formatNumber(mediaRows.length)} rows</span>
              </div>
              {mediaRows.length === 0 ? (
                <div className="source-empty"><Sparkles size={22} /><h2>No extracted rows yet</h2><p>Choose a file and run extraction to populate this staging grid.</p></div>
              ) : (
                <>
                  <div className="compact-table-shell media-review-table">
                    <table>
                      <thead><tr><th>Email</th><th>Name</th><th>Company</th><th>Website</th><th>Country</th><th>Industry</th><th>Status</th></tr></thead>
                      <tbody>
                        {mediaRows.map((row, index) => (
                          <tr key={`${row.email}-${index}`}>
                            <td><input value={row.email ?? ''} onChange={(event) => updateMediaRow(index, 'email', event.target.value)} /></td>
                            <td><input value={row.fullName ?? ''} onChange={(event) => updateMediaRow(index, 'fullName', event.target.value)} /></td>
                            <td><input value={row.company ?? ''} onChange={(event) => updateMediaRow(index, 'company', event.target.value)} /></td>
                            <td><input value={row.website ?? ''} onChange={(event) => updateMediaRow(index, 'website', event.target.value)} /></td>
                            <td><input value={row.country ?? ''} onChange={(event) => updateMediaRow(index, 'country', event.target.value)} /></td>
                            <td><input value={row.industry ?? ''} onChange={(event) => updateMediaRow(index, 'industry', event.target.value)} /></td>
                            <td><select value={row.status ?? mediaDefaultStatus} onChange={(event) => updateMediaRow(index, 'status', event.target.value)}>{leadStatuses.map((status) => <option key={status} value={status}>{statusLabels[status]}</option>)}</select></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="source-card-actions"><button className="ghost-button" type="button" onClick={() => setMediaRows([])}>Clear staging</button><button className="primary-button" type="button" disabled={!canWrite || busyAction === 'mediaImport'} onClick={handleMediaImport}><Upload size={17} />Save reviewed rows</button></div>
                </>
              )}
            </section>
          </section>
        )}

        {activeView === 'dedupe' && (
          <>
            <section className="operation-panel">
              <div className="panel-heading">
                <div>
                  <h2>Run deduplication</h2>
                  <p>Negative replies are always excluded, regardless of the selected period.</p>
                </div>
              </div>
              <div className="panel-grid dedupe-grid">
                <label>
                  CSV file
                  <input type="file" accept=".csv,.txt" onChange={(event) => setDedupeFile(event.target.files?.[0] ?? null)} />
                </label>
                <label>
                  Match sent data within
                  <select value={dedupeWindow} onChange={(event) => setDedupeWindow(event.target.value === 'all' ? 'all' : Number(event.target.value))}>
                    {Array.from({ length: 12 }, (_, index) => index + 1).map((months) => (
                      <option key={months} value={months}>{months} {months === 1 ? 'month' : 'months'}</option>
                    ))}
                    <option value="all">All time</option>
                  </select>
                </label>
                <button className="primary-button" type="button" disabled={!canWrite || busyAction === 'dedupe'} onClick={handleDeduplication}>
                  <ShieldCheck size={17} />
                  Clean List
                </button>
                <label className="wide-field">
                  Paste emails or CSV rows
                  <textarea value={dedupeText} onChange={(event) => setDedupeText(event.target.value)} placeholder="Paste up to 500,000 emails or mixed CSV/text rows. The tool extracts email IDs automatically.&#10;alex@example.com, Sent, 2026-09-30&#10;not interested: sam@example.com" />
                </label>
              </div>
            </section>

            <JobsPanel jobs={jobs.filter((job) => job.jobType === 'deduplication')} onDownload={safeDownload} onOpen={openJobDetails} title="Deduplication jobs" />
          </>
        )}

        {activeView === 'sources' && (
          <section className="source-history" aria-label="Data source history">
            {sourceCatalogError ? (
              <div className="operation-panel source-empty" role="alert">
                <FolderArchive size={22} />
                <h2>Saved sources could not be loaded</h2>
                <p>{sourceCatalogError}</p>
                <button className="ghost-button" type="button" onClick={() => setRefreshKey(value => value + 1)}><RefreshCw size={16} />Retry loading files</button>
              </div>
            ) : !sourceCatalogLoading && dataSources.length === 0 && !sourceQuery.trim() && sourceKindFilter === 'all' && sourceReplyFilter === 'all' ? (
              <div className="operation-panel source-empty">
                <FolderArchive size={22} />
                <h2>No saved sources yet</h2>
                <p>Imported files and pasted lead batches will appear here.</p>
              </div>
            ) : (
              <>
                <section className="source-catalog-toolbar" aria-label="Source directory controls">
                  <div><span className="section-kicker"><FolderArchive size={14} /> Source catalog</span><strong>{sourceCatalogLoading ? 'Searching saved sources...' : `Showing ${formatNumber(visibleSources.length)} saved sources · ${formatNumber(loadedSourceLeadCount)} unique leads`}</strong></div>
                  <label className="source-search"><Search size={16} /><input value={sourceQuery} onChange={(event) => setSourceQuery(event.target.value)} placeholder="Search all saved files, servers, or dates" />{sourceQuery && <button className="source-search-clear" type="button" aria-label="Clear source search" title="Clear search" onClick={() => setSourceQuery('')}><X size={15} /></button>}</label>
                  <label><span className="sr-only">Source type</span><select value={sourceKindFilter} onChange={(event) => setSourceKindFilter(event.target.value as 'all' | DataSource['kind'])}><option value="all">All source types</option><option value="file">Uploaded files</option><option value="paste">Pasted batches</option><option value="manual">Manual entries</option></select></label>
                  <label><span className="sr-only">Reply outcomes</span><select value={sourceReplyFilter} onChange={event => setSourceReplyFilter(event.target.value as typeof sourceReplyFilter)}><option value="all">All reply outcomes</option><option value="positive">Has positive replies</option><option value="negative">Has negative replies</option><option value="both">Has both replies</option><option value="no_positive">No positive replies</option><option value="no_negative">No negative replies</option></select></label>
                </section>
                <section className={`source-catalog-layout ${mobileSourceDetail ? 'mobile-detail-open' : ''}`}>
                  <aside className="source-directory" aria-label="Saved files directory">
                    <header><strong>Files & batches</strong><span>{formatNumber(visibleSources.length)} shown</span></header>
                    <div className="source-directory-list" ref={sourceDirectoryListRef} onScroll={handleSourceDirectoryScroll}>
                      {visibleSources.map((source) => {
                        const selected = sourceKey(source) === sourceKey(activeCatalogSource ?? source);
                        return <button className={`source-directory-item ${selected ? 'selected' : ''}`} type="button" key={sourceKey(source)} data-source-key={sourceKey(source)} onClick={(event) => { event.currentTarget.scrollIntoView({ block: 'start', behavior: 'auto' }); sourceDirectoryActiveKeyRef.current = sourceKey(source); setActiveCatalogSourceKey(sourceKey(source)); setMobileSourceDetail(true); }}>
                          <span className={`source-kind ${source.kind}`}>{source.kind === 'paste' ? <FileText size={16} /> : <FolderArchive size={16} />}</span>
                          <span><strong>{source.sourceFile}</strong><small>{formatDate(source.uploadDate)} · {formatNumber(source.leadCount)} unique / {formatNumber(source.originalRows)} processed</small>{source.countsPending ? <small>{source.countsError ? 'Select file to retry live counts' : 'Loading live counts…'}</small> : <span className="source-mini-metrics"><b>{formatNumber(source.positiveCount)} positive</b><b>{formatNumber(source.negativeCount)} negative</b><b>{formatNumber(source.failedCount)} failed</b><b>{formatNumber(source.suppressedCount)} suppressed</b></span>}</span>
                          {!source.countsPending && <em title="Positive / Negative">{formatNumber(source.positiveCount)}+ · {formatNumber(source.negativeCount)}-</em>}
                        </button>;
                      })}
                      {visibleSources.length === 0 && <div className="source-directory-empty">{sourceCatalogLoading ? 'Searching saved sources...' : 'No saved sources match these filters.'}</div>}
                      {sourceNextCursor && <div ref={sourceMoreRef} className="source-load-more" aria-live="polite"><button className="ghost-button" type="button" disabled={sourceLoadingMore} onClick={() => void loadOlderSources()}>{sourceLoadingMore ? 'Loading earlier files...' : 'Load more files'}</button></div>}
                    </div>
                  </aside>
                  {activeCatalogSource ? <article className="source-detail-panel">
                    <button type="button" className="ghost-button mobile-source-back" onClick={() => setMobileSourceDetail(false)}><ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />Files & batches</button>
                    <header className="source-detail-header">
                      <div className="source-detail-title"><span className={`source-kind ${activeCatalogSource.kind}`}>{activeCatalogSource.kind === 'paste' ? <FileText size={19} /> : <FolderArchive size={19} />}</span><div><span className="section-kicker">{activeCatalogSource.kind === 'paste' ? 'Pasted batch' : activeCatalogSource.kind === 'manual' ? 'Manual entry' : 'Uploaded file'}</span><h2>{activeCatalogSource.sourceFile}</h2><p>Uploaded {formatDate(activeCatalogSource.uploadDate)} · {activeCatalogSource.sheetCount || 1} {activeCatalogSource.sheetCount === 1 ? 'sheet' : 'sheets'}</p></div></div>
                      <div className="source-card-actions"><button className="ghost-button" type="button" disabled={!canWrite || busyAction === `enrich:${activeCatalogSource.sourceJobId ?? activeCatalogSource.sourceFile}:${activeCatalogSource.uploadDate}`} onClick={() => void handleSourceEnrichment(activeCatalogSource)}><Sparkles size={16} />Enrich</button><button className="ghost-button" type="button" disabled={!canWrite} onClick={() => setFollowUpSource(activeCatalogSource)}><CalendarClock size={16} />Follow-ups</button><button className="primary-button" type="button" onClick={() => openSource(activeCatalogSource)}>Open full sheet<ChevronRight size={16} /></button></div>
                    </header>
                    <div className="source-detail-total"><span>Processed rows</span><strong>{formatNumber(activeCatalogSource.originalRows)}</strong><small>{formatNumber(activeCatalogSource.leadCount)} unique · {formatNumber(activeCatalogSource.insertedRows)} new · {formatNumber(activeCatalogSource.importUpdatedRows)} matched existing</small></div>
                    {activeSourceSummaryError ? <div className="source-load-more" role="alert">{activeSourceSummaryError} <button className="ghost-button" type="button" onClick={() => setSourceSummaryRevision((value) => value + 1)}><RefreshCw size={16} />Retry</button></div> : activeCatalogSourceOutcomes && !activeSourceSummaryLoading ? <><div className="source-metrics source-detail-metrics"><span><strong>{formatNumber(activeCatalogSourceOutcomes.unsentCount)}</strong>Unsent</span><span><strong>{formatNumber(activeCatalogSourceOutcomes.sentCount)}</strong>Sent</span><span className="metric-positive"><strong>{formatNumber(activeCatalogSourceOutcomes.positiveCount)}</strong>Positive</span><span className="metric-negative"><strong>{formatNumber(activeCatalogSourceOutcomes.negativeCount)}</strong>Negative</span><span className="metric-failed"><strong>{formatNumber(activeCatalogSourceOutcomes.failedCount)}</strong>Failed</span><span><strong>{formatNumber(activeCatalogSourceOutcomes.junkCount + activeCatalogSourceOutcomes.hardBounceCount)}</strong>Junk / bounce</span></div><SourceStatusChart source={activeCatalogSourceOutcomes} /></> : <div className="source-load-more">Loading live source results...</div>}
                    {activeCatalogSource.sourceJobId && <SourceSheets key={activeCatalogSource.sourceJobId} jobId={activeCatalogSource.sourceJobId} onOpen={sheet => openSource(activeCatalogSource, sheet)} onDownload={safeDownload} />}
                  </article> : <article className="source-detail-panel source-empty"><Search size={22} /><h2>No source matches this search</h2><p>Try another file name, server IP, date or source type.</p></article>}
                </section>
              </>
            )}
            <JobsPanel
              jobs={jobs.filter((job) => job.jobType === 'source_enrichment')}
              onDownload={safeDownload}
              onOpen={openJobDetails}
              title="Enrichment jobs"
              subtitle="Source-level enrichment progress and downloadable enriched files."
            />
          </section>
        )}

        {activeView === 'followups' && (
          <section className="follow-up-workspace" aria-label="Follow-up schedule">
            <div className="follow-up-summary-grid">
              <article><span>Due today</span><strong>{formatNumber(followUpSummary.dueToday)}</strong></article>
              <article><span>Overdue</span><strong>{formatNumber(followUpSummary.overdue)}</strong></article>
              <article><span>Upcoming</span><strong>{formatNumber(followUpSummary.upcoming)}</strong></article>
            </div>
            <section className="operation-panel">
              <div className="panel-heading">
                <h2>Scheduled follow-ups</h2>
                <span>{formatNumber(followUps.length)} saved tasks</span>
              </div>
              {followUps.length === 0 ? (
                <div className="source-empty">
                  <CalendarClock size={22} />
                  <h2>No follow-ups scheduled</h2>
                  <p>Open a data source and save a follow-up plan for its sent list.</p>
                </div>
              ) : (
                <div className="compact-table-shell follow-up-table">
                  <table>
                    <thead><tr><th>Source</th><th>Step</th><th>Sent date</th><th>Due date</th><th>Status</th><th>Action</th></tr></thead>
                    <tbody>
                      {followUps.map((task) => (
                        <tr key={task.id}>
                          <td><strong>{task.sourceLabel}</strong></td>
                          <td>Follow-up {task.sequence}</td>
                          <td>{formatDate(task.sentDate)}</td>
                          <td>{formatDate(task.dueDate)}</td>
                          <td><span className={`follow-up-status ${task.status}`}>{task.status}</span></td>
                          <td>
                            {task.status === 'pending' ? (
                              <div className="follow-up-actions">
                                <button type="button" disabled={!canWrite || busyAction === `followUp:${task.id}`} onClick={() => void changeFollowUpStatus(task.id, 'completed')}>Done</button>
                                <button type="button" disabled={!canWrite || busyAction === `followUp:${task.id}`} onClick={() => void changeFollowUpStatus(task.id, 'dismissed')}>Skip</button>
                              </div>
                            ) : (
                              <span className="muted-value">{task.completedAt ? formatDate(task.completedAt) : '-'}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </section>
        )}

        {activeView === 'inboxSync' && (
          <section className="inbox-sync-workspace" aria-label="Inbox PST synchronization">
            <section className="operation-panel">
              <div className="panel-heading">
                <div>
                  <h2>Outlook PST negative sync</h2>
                  <p>Select multiple PST archives and the tool will upload them in resumable chunks, then detect only negative, junk, hard bounce and failed outcomes.</p>
                </div>
                <span className={`follow-up-status ${pstParserStatus?.installed ? 'completed' : 'dismissed'}`}>
                  PST parser: {pstParserStatus?.installed ? 'Ready' : 'Not installed'}
                </span>
              </div>
              <div className="inbox-sync-grid">
                <div
                  className={`drop-zone pst-drop-zone ${pstDragActive ? 'active' : ''}`}
                  onDragOver={(event) => { event.preventDefault(); setPstDragActive(true); }}
                  onDragLeave={() => setPstDragActive(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setPstDragActive(false);
                    addPstFiles(event.dataTransfer.files);
                  }}
                >
                  <MailCheck size={30} />
                  <strong>Drop PST files here</strong>
                  <span>Multiple .pst files supported. Upload pauses offline and resumes from missing chunks when internet returns.</span>
                  <input type="file" accept=".pst" multiple onChange={(event) => { if (event.target.files) addPstFiles(event.target.files); event.currentTarget.value = ''; }} />
                </div>
                <div className="inbox-sync-controls">
                  <label>PST import date<input type="date" value={pstUploadDate} onChange={(event) => setPstUploadDate(event.target.value)} /></label>
                  <label>Fallback mail date<input type="date" value={pstFallbackDate} onChange={(event) => setPstFallbackDate(event.target.value)} /></label>
                  <div className="format-note">Positive reply detection is disabled. Existing Positive, Negative and Failed outcomes are protected from overwrite.</div>
                  {pstParserStatus && !pstParserStatus.installed && <div className="notice"><AlertTriangle size={17} />{pstParserStatus.message ?? 'PST parser readpst is required before processing can complete.'}</div>}
                  <button className="primary-button" type="button" disabled={!canWrite || busyAction === 'outlookPstQueue' || pstQueue.length === 0} onClick={() => void startPstQueueSync()}>
                    <Upload size={17} />
                    Start / Resume Queue
                  </button>
                </div>
              </div>
            </section>

            <section className="operation-panel">
              <div className="panel-heading">
                <div><h2>PST sync queue</h2><p>Each archive uploads in chunks first, then creates a negative-only status update job.</p></div>
                <span>{formatNumber(pstQueue.length)} files</span>
              </div>
              {pstQueue.length === 0 ? (
                <div className="source-empty"><MailCheck size={22} /><h2>No PST files selected</h2><p>Choose one or more Outlook PST files to start a resumable sync queue.</p></div>
              ) : (
                <div className="compact-table-shell">
                  <table>
                    <thead><tr><th>File</th><th>Progress</th><th>Status</th><th>Action</th></tr></thead>
                    <tbody>
                      {pstQueue.map((item) => (
                        <tr key={item.id}>
                          <td><strong>{item.name}</strong><small className="table-subtle">{formatBytes(item.size)} · {formatNumber(item.uploadedChunks)} / {formatNumber(item.chunkCount)} chunks</small></td>
                          <td><div className="progress-bar"><span style={{ width: `${Math.min(100, Math.max(0, item.progress))}%` }} /></div><small className="table-subtle">{Math.round(item.progress)}%</small></td>
                          <td><span className={`follow-up-status ${item.status === 'completed' ? 'completed' : item.status === 'failed' ? 'dismissed' : 'pending'}`}>{item.status}</span>{item.error && <small className="table-subtle">{item.error}</small>}{item.jobId && <small className="table-subtle">Job {item.jobId.slice(0, 8)}</small>}</td>
                          <td><div className="follow-up-actions"><button className="ghost-button" type="button" disabled={!canWrite || busyAction === 'outlookPstQueue' || item.status === 'completed'} onClick={() => void startPstQueueSync(item.id)}><RefreshCcw size={15} />Resume</button><button type="button" disabled={busyAction === 'outlookPstQueue'} onClick={() => setPstQueue((current) => current.filter((candidate) => candidate.id !== item.id))}>Remove</button></div></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <StatusUpdateBatchesPanel batches={statusBatches.filter((batch) => batch.fileName.toLowerCase().includes('outlook pst'))} onOpen={openStatusBatchReport} />
            <JobsPanel jobs={jobs.filter((job) => job.options?.source === 'outlook_pst' || job.fileName.toLowerCase().includes('outlook pst'))} onDownload={safeDownload} onOpen={openJobDetails} title="PST sync jobs" subtitle="Negative-only Outlook archive processing history." />
          </section>
        )}

        {activeView === 'serverSync' && (
          <section className="follow-up-workspace" aria-label="Mail server synchronization">
            <section className="operation-panel server-connector-panel">
              <div className="panel-heading">
                <div>
                  <h2>Connect mail server</h2>
                  <p>Use the PowerMTA Logs page URL, including its custom port when present. HTTP and HTTPS accounting monitors are supported.</p>
                </div>
              </div>
              <div className="panel-grid campaign-form">
                <label>Server name<input value={mailLogName} onChange={(event) => setMailLogName(event.target.value)} placeholder="Accounting server" /></label>
                <label className="wide-field">PowerMTA logs URL<input value={mailLogUrl} onChange={(event) => setMailLogUrl(event.target.value)} placeholder="https://server.example:2525/ui/logs" /></label>
                <label>Auto-sync interval<select value={mailLogInterval} onChange={(event) => setMailLogInterval(Number(event.target.value))}><option value={15}>Every 15 minutes</option><option value={30}>Every 30 minutes</option><option value={60}>Every hour</option><option value={360}>Every 6 hours</option></select></label>
                <label>Sync history (days)<input type="number" min={1} max={365} value={mailLogLookback} onChange={(event) => setMailLogLookback(Math.min(365, Math.max(1, Number(event.target.value) || 1)))} /></label>
                <label className="checkbox-field"><input type="checkbox" checked={mailLogInsecureTls} onChange={(event) => setMailLogInsecureTls(event.target.checked)} /> Accept this server's private TLS certificate</label>
                <button className="primary-button" type="button" disabled={!canWrite || busyAction === 'mailLogCreate'} onClick={handleCreateMailLogSource}><ServerCog size={17} />{editingMailLogSource ? 'Save server' : 'Connect server'}</button>
                {editingMailLogSource && <button className="ghost-button" type="button" onClick={() => { setEditingMailLogSource(null); setMailLogName(''); setMailLogUrl(''); setMailLogLookback(7); }}>Cancel edit</button>}
              </div>
            </section>
            <section className="operation-panel server-list-panel">
              <div className="panel-heading server-sync-heading">
                <div><h2>Connected servers</h2><p>Select multiple servers and run their accounting sync from one place.</p></div>
                <span>{formatNumber(mailLogSources.length)} sources</span>
              </div>
              {mailLogSources.length === 0 ? <div className="source-empty"><ServerCog size={22} /><h2>No mail servers connected</h2><p>Connect a PowerMTA logs URL to automate accounting and logging-file status updates.</p></div> : (
                <>
                  <div className="server-batch-toolbar">
                    <label className="server-select-all"><input type="checkbox" checked={mailLogSources.length > 0 && selectedMailLogSourceIds.size === mailLogSources.length} onChange={toggleAllMailLogSources} /> Select all</label>
                    <span>{selectedMailLogSourceIds.size} selected</span>
                    <button className="ghost-button" type="button" disabled={!canWrite || busyAction === 'mailLogBatch' || selectedMailLogSourceIds.size === 0} onClick={() => void handleSelectedMailLogSync()}><RefreshCcw size={15} />Sync selected</button>
                    <button className="primary-button" type="button" disabled={!canWrite || busyAction === 'mailLogBatch' || selectedMailLogSourceIds.size === 0} onClick={() => void handleSelectedMailLogSync(true)}><ServerCog size={16} />Full re-sync selected</button>
                    <button className="deep-sync-button" type="button" disabled={!canWrite || busyAction === 'mailLogBatch' || mailLogSources.length === 0} onClick={() => void handleSelectedMailLogSync(true, true, mailLogSources)}><Database size={16} />Deep sync all servers</button>
                  </div>
                  <p className="server-sync-hint">Sync selected reads only changed accounting files. Full re-sync uses each server's configured history window. Deep sync re-reads every CSV currently listed by every connected server.</p>
                  {mailLogBatchProgress && <div className={`server-sync-progress ${mailLogBatchProgress.completed === mailLogBatchProgress.total ? 'complete' : ''}`}><strong>{busyAction === 'mailLogBatch' ? `Syncing server ${mailLogBatchProgress.completed + 1} of ${mailLogBatchProgress.total}` : `Batch complete: ${mailLogBatchProgress.completed} of ${mailLogBatchProgress.total} servers`}</strong><span>{formatNumber(mailLogBatchProgress.processedFiles)} mail files processed · {formatNumber(mailLogBatchProgress.updatedRows)} lead updates</span>{mailLogBatchProgress.failures.length > 0 && <small>{mailLogBatchProgress.failures.join(' | ')}</small>}</div>}
                  <div className="compact-table-shell"><table><thead><tr><th><input type="checkbox" aria-label="Select all servers" checked={mailLogSources.length > 0 && selectedMailLogSourceIds.size === mailLogSources.length} onChange={toggleAllMailLogSources} /></th><th>Server</th><th>Schedule</th><th>Progress</th><th>Last sync</th><th>Result</th><th>Action</th></tr></thead><tbody>{mailLogSources.map((source) => <tr key={source.id} className={selectedMailLogSourceIds.has(source.id) ? 'selected-server-row' : undefined}><td><input type="checkbox" aria-label={`Select ${source.name}`} checked={selectedMailLogSourceIds.has(source.id)} onChange={() => toggleMailLogSourceSelection(source.id)} /></td><td><strong>{source.name}</strong><small className="table-subtle">{source.portalUrl}</small></td><td>Every {source.intervalMinutes} min<br /><span className="muted-value">{nextSyncLabel(source)}</span><br /><span className="muted-value">Last {source.lookbackDays} days</span></td><td><MailLogSyncProgress source={source} /></td><td>{source.lastSyncAt ? formatDate(source.lastSyncAt) : 'Not synced yet'}</td><td><span className={`follow-up-status ${source.lastSyncStatus === 'failed' ? 'dismissed' : source.lastSyncStatus === 'completed' ? 'completed' : 'pending'}`}>{source.lastSyncMessage ?? 'Waiting for first sync'}</span></td><td><div className="follow-up-actions"><button className="ghost-button" type="button" disabled={!canWrite || busyAction === 'mailLogBatch' || busyAction === `mailLog:${source.id}`} onClick={() => void handleMailLogSync(source)}><RefreshCcw size={15} />Sync now</button><button type="button" disabled={!canWrite || busyAction === 'mailLogBatch'} onClick={() => startEditingMailLogSource(source)}>Edit</button><button type="button" disabled={!canWrite || busyAction === 'mailLogBatch' || busyAction === `mailLogDelete:${source.id}`} onClick={() => void handleDeleteMailLogSource(source)}>Delete</button></div></td></tr>)}</tbody></table></div>
                </>
              )}
            </section>
          </section>
        )}

        {activeView === 'enrichmentHub' && (
          <section className="enrichment-hub" aria-label="Enrichment hub">
            <div className="enrichment-workspace-summary" aria-live="polite" aria-busy={enrichmentSummaryLoading}>
              <button type="button" onClick={() => openEnrichmentLeads()}>
                <span>Total data</span>
                <strong>{enrichmentSummary ? formatNumber(enrichmentSummary.totalLeads) : '—'}</strong>
                <small>All workspace leads</small>
              </button>
              <button type="button" className="enrichment-success" onClick={() => openEnrichmentLeads('saved')}>
                <span>Scans saved</span>
                <strong>{enrichmentSummary ? formatNumber(enrichmentSummary.enrichedLeads) : '—'}</strong>
                <small>Profile scan saved</small>
              </button>
              <button type="button" className="enrichment-failed" onClick={() => openEnrichmentLeads('failed')}>
                <span>Retrieval failed</span>
                <strong>{enrichmentSummary ? formatNumber(enrichmentSummary.failedLeads) : '—'}</strong>
                <small>Website fetch needs retry</small>
              </button>
              <button type="button" className="enrichment-pending" onClick={() => openEnrichmentLeads('pending')}>
                <span>Not yet enriched</span>
                <strong>{enrichmentSummary ? formatNumber(enrichmentSummary.remainingLeads) : '—'}</strong>
                <small>Still awaiting a profile scan</small>
              </button>
              <button type="button" onClick={() => document.getElementById('enrichment-jobs')?.scrollIntoView({ behavior: 'smooth' })}>
                <span>Jobs now</span>
                <strong>{enrichmentSummary ? formatNumber(enrichmentSummary.activeJobs) : formatNumber(enrichmentActiveCount)}</strong>
                <small>{enrichmentSummary ? `${formatNumber(enrichmentSummary.queuedJobs)} queued` : 'Loading queue'}</small>
              </button>
            </div>
            <EnrichmentQueue onOpen={id => { void getJob(id).then(setActiveJob).catch(error => setNotice(error.message)); }} />
            {enrichmentSummary?.fieldCoverage && <section className="lead-command-strip" aria-label="Publisher evidence coverage">
              <strong>Fields with publisher evidence</strong>
              {['company','phone','city','country','industry','employeeSize'].map(field => <span key={field}>{field}: {formatNumber(enrichmentSummary.fieldCoverage?.[field] ?? 0)}</span>)}
            </section>}
            {enrichmentSummary && <div className="lead-command-strip">
              <button type="button" onClick={() => openEnrichmentLeads('full')}>Full profiles: {formatNumber(enrichmentSummary.fullyEnrichedLeads)}</button>
              <button type="button" onClick={() => openEnrichmentLeads('partial')}>Partial: {formatNumber(enrichmentSummary.partialLeads)}</button>
              <button type="button" onClick={() => openEnrichmentLeads('no_data')}>No website data: {formatNumber(enrichmentSummary.noDataLeads)}</button>
              <button type="button" onClick={() => openEnrichmentLeads('legacy')}>Earlier scans: {formatNumber(enrichmentSummary.legacyScannedLeads)}</button>
              <span>Counts as of {new Date(enrichmentSummary.calculatedAt).toLocaleTimeString()}</span>
            </div>}
            <section className="operation-panel">
              <div className="panel-heading">
                <div>
                  <h2 id="enrichment-jobs">Enrichment lifecycle</h2>
                  <p>Website/domain scans preserve existing fields and fill missing website, company, phone, location and service-based industry details.</p>
                </div>
                <div className="source-card-actions">
                  <button className="ghost-button" type="button" onClick={() => setActiveView('sources')}><FolderArchive size={16} />Open source catalog</button>
                  <button className="primary-button" type="button" disabled={!canWrite || busyAction === 'enrich:workspace-missing'} onClick={() => void handleWorkspaceMissingEnrichment()}><Sparkles size={16} />Enrich missing details</button>
                  <button className="ghost-button" type="button" disabled={!canWrite || busyAction === 'enrich:retail-industry'} onClick={() => void handleRetailIndustryVerification()}><ShieldCheck size={16} />Recheck Retail industries</button>
                </div>
              </div>
              <JobsPanel
                jobs={enrichmentJobs}
                onDownload={safeDownload}
                onOpen={openJobDetails}
                title="Enrichment jobs"
                subtitle="Source-level enrichment progress, remaining rows and downloadable enriched files."
              />
            </section>
            <section className="operation-panel">
              <div className="panel-heading">
                <div><h2>Start from a source</h2><p>Pick a saved file or synced daily source, then start enrichment or open its full sheet.</p></div>
                <span>{formatNumber(dataSources.length)} loaded sources</span>
              </div>
              <div className="source-card-grid">
                {dataSources.slice(0, 12).map((source) => (
                  <article className="source-mini-card" key={sourceKey(source)}>
                    <span className="section-kicker">{source.kind === 'paste' ? 'Pasted batch' : source.kind === 'manual' ? 'Manual entry' : 'Data source'}</span>
                    <h3>{source.sourceFile}</h3>
                    <p>{formatDate(source.uploadDate)} · {formatNumber(source.leadCount)} leads</p>
                    <div className="source-card-actions">
                      <button className="ghost-button" type="button" disabled={!canWrite || busyAction === `enrich:${source.sourceJobId ?? source.sourceFile}:${source.uploadDate}`} onClick={() => void handleSourceEnrichment(source)}><Sparkles size={16} />Enrich</button>
                      <button className="primary-button" type="button" onClick={() => openSource(source)}>Open sheet<ChevronRight size={16} /></button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </section>
        )}

        {activeView === 'campaigns' && (
          <section className="operation-panel">
            <div className="panel-heading">
              <h2>Campaigns</h2>
            </div>
            <div className="panel-grid campaign-form">
              <label>
                Name
                <input value={newCampaignName} onChange={(event) => setNewCampaignName(event.target.value)} placeholder="September outreach" />
              </label>
              <label>
                Description
                <input value={newCampaignDescription} onChange={(event) => setNewCampaignDescription(event.target.value)} placeholder="Segment, offer, or source" />
              </label>
              <button className="primary-button" type="button" disabled={!canWrite || busyAction === 'campaign'} onClick={handleCreateCampaign}>
                <ListChecks size={17} />
                Save Campaign
              </button>
            </div>
            <div className="compact-table-shell">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Leads</th>
                    <th>Sent</th>
                    <th>Positive</th>
                    <th>Suppressed</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => (
                    <tr key={campaign.id}>
                      <td>
                        <strong>{campaign.name}</strong>
                        <span>{campaign.id}</span>
                      </td>
                      <td>{campaign.status}</td>
                      <td>{formatNumber(campaign.leadCount)}</td>
                      <td>{formatNumber(campaign.sentCount)}</td>
                      <td>{formatNumber(campaign.positiveCount)}</td>
                      <td>{formatNumber(campaign.suppressedCount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {activeView === 'analytics' && (
          <>
            <section className="analytics-grid">
              <article className="operation-panel">
                <div className="panel-heading">
                  <h2>Daily lead intake</h2>
                </div>
                <div className="bar-list">
                  {(analytics?.dailyLeadActivity ?? []).map((row) => (
                    <div className="bar-row" key={row.day}>
                      <span>{formatDate(row.day)}</span>
                      <div>
                        <i style={{ width: `${(row.createdCount / maxDailyLeads) * 100}%` }} />
                      </div>
                      <strong>{formatNumber(row.createdCount)}</strong>
                    </div>
                  ))}
                </div>
              </article>
              <article className="operation-panel">
                <div className="panel-heading">
                  <h2>Campaign performance</h2>
                </div>
                <div className="compact-table-shell">
                  <table>
                    <thead>
                      <tr>
                        <th>Campaign</th>
                        <th>Leads</th>
                        <th>Sent</th>
                        <th>Positive</th>
                        <th>Failed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(analytics?.campaignPerformance ?? []).map((campaign) => (
                        <tr key={campaign.id}>
                          <td>{campaign.name}</td>
                          <td>{formatNumber(campaign.leadCount)}</td>
                          <td>{formatNumber(campaign.sentCount)}</td>
                          <td>{formatNumber(campaign.positiveCount)}</td>
                          <td>{formatNumber(campaign.failedCount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </article>
            </section>
          </>
        )}

        {activeView === 'performance' && (
          <section className="performance-workspace" aria-label="System performance monitor">
            <div className="follow-up-summary-grid">
              <article><span>Tracked routes</span><strong>{formatNumber(performance?.routes.length ?? 0)}</strong></article>
              <article><span>Slow threshold</span><strong>{formatNumber(performance?.slowThresholdMs ?? 750)} ms</strong></article>
              <article><span>Slow requests</span><strong>{formatNumber(performance?.recentSlowRequests.length ?? 0)}</strong></article>
            </div>
            <section className="operation-panel">
              <div className="panel-heading">
                <div><h2>Route timing</h2><p>{performance?.capturedAt ? `Captured ${formatDateTime(performance.capturedAt)}` : 'Timing appears after authenticated API calls run.'}</p></div>
                <button className="ghost-button" type="button" onClick={() => void getPerformanceSnapshot().then(setPerformance)}><RefreshCcw size={16} />Refresh timings</button>
              </div>
              <div className="compact-table-shell">
                <table>
                  <thead><tr><th>Route</th><th>Calls</th><th>Average</th><th>Max</th><th>Slow</th></tr></thead>
                  <tbody>
                    {(performance?.routes ?? []).map((route) => (
                      <tr key={route.path}>
                        <td><strong>{route.path}</strong></td>
                        <td>{formatNumber(route.calls)}</td>
                        <td>{formatNumber(route.averageMs)} ms</td>
                        <td>{formatNumber(route.maxMs)} ms</td>
                        <td>{formatNumber(route.slowCalls)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <section className="operation-panel">
              <div className="panel-heading">
                <h2>Recent slow requests</h2>
              </div>
              {(performance?.recentSlowRequests.length ?? 0) === 0 ? (
                <div className="source-empty"><CheckCircle2 size={22} /><h2>No slow API calls captured</h2></div>
              ) : (
                <div className="compact-table-shell">
                  <table>
                    <thead><tr><th>Request</th><th>Status</th><th>Duration</th><th>Time</th></tr></thead>
                    <tbody>
                      {performance?.recentSlowRequests.map((request) => (
                        <tr key={`${request.method}-${request.path}-${request.createdAt}`}>
                          <td><strong>{request.method} {request.path}</strong></td>
                          <td>{request.statusCode}</td>
                          <td>{formatNumber(request.durationMs)} ms</td>
                          <td>{formatDateTime(request.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </section>
        )}

        {activeView === 'suppression' && (
          <section className="operation-panel">
            <div className="panel-heading">
              <h2>Suppression list</h2>
            </div>
            <div className="compact-table-shell">
              <table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Reason</th>
                    <th>Source</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {suppression.map((row) => (
                    <tr key={row.emailNorm}>
                      <td>{row.email}</td>
                      <td>{statusLabels[row.reason]}</td>
                      <td>{row.source}</td>
                      <td>{formatDate(row.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {activeView === 'audit' && canAudit && (
          <section className="operation-panel">
            <div className="panel-heading">
              <h2>Audit log</h2>
            </div>
            <div className="compact-table-shell">
              <table>
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>User</th>
                    <th>Target</th>
                    <th>IP</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((row) => (
                    <tr key={row.id}>
                      <td>{row.action}</td>
                      <td>{row.userName || row.userEmail || 'System'}</td>
                      <td>{row.entityType}{row.entityId ? `:${row.entityId}` : ''}</td>
                      <td>{row.ipAddress || '-'}</td>
                      <td>{formatDate(row.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {activeView === 'backups' && canAdmin && <Backups />}
        {activeView === 'settings' && (
          <section className="settings-grid">
            {canAdmin && <EnrichmentProxySettings />}
            <form className="operation-panel" onSubmit={handleChangePassword}>
              <div className="panel-heading">
                <h2>Change password</h2>
              </div>
              <div className="stacked-form">
                <label>
                  Current password
                  <input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} />
                </label>
                <label>
                  New password
                  <input type="password" value={nextPassword} onChange={(event) => setNextPassword(event.target.value)} />
                </label>
                <button className="primary-button" type="submit" disabled={busyAction === 'password'}>
                  <KeyRound size={17} />
                  Update Password
                </button>
              </div>
            </form>

            {canAdmin && (
              <section className="operation-panel">
              <div className="panel-heading">
                <h2>Users</h2>
              </div>
                <p className="workspace-note">Each user receives a private workspace. Organization duplicate and suppression checks remain active without showing another user&apos;s lead details.</p>
                <div className="panel-grid user-form">
                  <label>
                    Email
                    <input value={newUserEmail} onChange={(event) => setNewUserEmail(event.target.value)} />
                  </label>
                  <label>
                    Name
                    <input value={newUserName} onChange={(event) => setNewUserName(event.target.value)} />
                  </label>
                  <label>
                    Workspace name
                    <input value={newUserWorkspace} onChange={(event) => setNewUserWorkspace(event.target.value)} placeholder="Private workspace" />
                  </label>
                  <label>
                    Role
                    <select value={newUserRole} onChange={(event) => setNewUserRole(event.target.value as UserRole)}>
                      {userRoles.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Password
                    <input type="password" value={newUserPassword} onChange={(event) => setNewUserPassword(event.target.value)} />
                  </label>
                  <button className="primary-button" type="button" disabled={busyAction === 'user'} onClick={handleCreateUser}>
                    <UserPlus size={17} />
                    Create User
                  </button>
                </div>
                <div className="compact-table-shell">
                  <table>
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Workspace</th>
                        <th>Role</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((appUser) => (
                        <tr key={appUser.id}>
                          <td>
                            <strong>{appUser.displayName}</strong>
                            <span>{appUser.email}</span>
                          </td>
                          <td>{appUser.workspaceName}</td>
                          <td>{appUser.role}</td>
                          <td>{appUser.active ? 'active' : 'disabled'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </section>
        )}
      </section>
      {commandOpen && (
        <div className="command-backdrop" onMouseDown={(event) => event.target === event.currentTarget && setCommandOpen(false)}>
          <section className="command-palette" role="dialog" aria-modal="true" aria-label="Command palette">
            <label className="command-search">
              <Search size={18} />
              <input autoFocus value={commandQuery} onChange={(event) => setCommandQuery(event.target.value)} placeholder="Search commands, email keyword, provider, export..." />
            </label>
            <div className="command-list">
              {commandActions.length === 0 ? (
                <div className="command-empty">No command matches this search.</div>
              ) : commandActions.map((action) => (
                <button key={action.label} type="button" onClick={() => { action.run(); setCommandOpen(false); }}>
                  <strong>{action.label}</strong>
                  <span>{action.detail}</span>
                </button>
              ))}
            </div>
          </section>
        </div>
      )}
      {singleLeadOpen && (
        <div className="drawer-backdrop" onMouseDown={(event) => event.target === event.currentTarget && busyAction !== 'singleLead' && setSingleLeadOpen(false)}>
          <form className="follow-up-dialog single-lead-dialog" onSubmit={handleSingleLeadSave}>
            <header>
              <div>
                <span className="section-kicker"><UserPlus size={14} /> Manual entry</span>
                <h2>Add single lead</h2>
                <p>Email is required; other fields can be completed now or edited later from the lead table.</p>
              </div>
              <button className="icon-button" type="button" onClick={() => setSingleLeadOpen(false)} disabled={busyAction === 'singleLead'} aria-label="Close single lead form"><X size={19} /></button>
            </header>
            <div className="follow-up-form-grid single-lead-grid">
              <label>Email<input required type="email" value={singleLead.email} onChange={(event) => setSingleLead((current) => ({ ...current, email: event.target.value }))} /></label>
              <label>Name<input value={singleLead.fullName ?? ''} onChange={(event) => setSingleLead((current) => ({ ...current, fullName: event.target.value }))} /></label>
              <label>Phone<input value={singleLead.phone ?? ''} onChange={(event) => setSingleLead((current) => ({ ...current, phone: event.target.value }))} /></label>
              <label>Website<input value={singleLead.website ?? ''} onChange={(event) => setSingleLead((current) => ({ ...current, website: event.target.value }))} /></label>
              <label>Company<input value={singleLead.company ?? ''} onChange={(event) => setSingleLead((current) => ({ ...current, company: event.target.value }))} /></label>
              <label>Designation<input value={singleLead.title ?? ''} onChange={(event) => setSingleLead((current) => ({ ...current, title: event.target.value }))} /></label>
              <label>Country<input list="country-options" value={singleLead.country ?? ''} onChange={(event) => setSingleLead((current) => ({ ...current, country: event.target.value }))} /></label>
              <label>City<input value={singleLead.city ?? ''} onChange={(event) => setSingleLead((current) => ({ ...current, city: event.target.value }))} /></label>
              <label>Industry<input value={singleLead.industry ?? ''} onChange={(event) => setSingleLead((current) => ({ ...current, industry: event.target.value }))} /></label>
              <label>Status<select value={singleLead.status ?? 'unsent'} onChange={(event) => setSingleLead((current) => ({ ...current, status: event.target.value as LeadStatus }))}>{leadStatuses.map((status) => <option key={status} value={status}>{statusLabels[status]}</option>)}</select></label>
            </div>
            <footer>
              <button className="ghost-button" type="button" onClick={() => setSingleLeadOpen(false)} disabled={busyAction === 'singleLead'}>Cancel</button>
              <button className="primary-button" type="submit" disabled={busyAction === 'singleLead'}><UserPlus size={17} />Save Lead</button>
            </footer>
          </form>
        </div>
      )}
      {activeLead && (
        <Suspense fallback={<div className="notice" role="status">Loading lead details...</div>}><LeadDetailsModal
          key={activeLead.id}
          lead={activeLead}
          campaigns={campaigns}
          canEdit={canWrite && !maskSensitive}
          saving={busyAction === 'leadDetails'}
          onClose={() => setActiveLead(null)}
          onSave={handleLeadSave}
        /></Suspense>
      )}
      {activeJob && <JobDetailsDialog
        job={activeJob}
        onClose={() => setActiveJob(null)}
        onDownload={safeDownload}
        onRollback={rollbackJob}
        onPause={(job) => void runEnrichmentJobAction(job, 'pause')}
        onResume={(job) => void runEnrichmentJobAction(job, 'resume')}
        onRetryFailed={(job) => void runEnrichmentJobAction(job, 'retry')}
      />}
      {activeStatusBatch && <StatusUpdateBatchDialog batch={activeStatusBatch} onClose={() => setActiveStatusBatch(null)} />}
      {followUpSource && (
        <FollowUpScheduler
          key={`${followUpSource.sourceJobId ?? followUpSource.sourceFile}-${followUpSource.uploadDate}`}
          source={followUpSource}
          saving={busyAction === 'followUpSchedule'}
          onClose={() => setFollowUpSource(null)}
          onSchedule={saveFollowUpPlan}
        />
      )}
    </main>
  );
}
